import React, { createContext, useContext, useEffect, useState, useCallback } from "react";

export const LANGS = [
  { code: "en", label: "English" },
  { code: "fr", label: "Français" },
  { code: "de", label: "Deutsch" },
  { code: "es", label: "Español" },
  { code: "it", label: "Italiano" },
];

// Translation dictionaries. Missing keys fall back to English, then to the key itself.
const dict = {
  en: {
    "nav.home": "Home",
    "nav.dashboard": "Dashboard",
    "nav.matches": "Matches",
    "nav.browse": "Browse cases",
    "nav.admin": "Admin",
    "nav.signin": "Sign in",
    "nav.signout": "Sign out",
    "nav.create_account": "Create account",
    "landing.eyebrow": "Missing & unidentified people across Europe",
    "landing.hero_title": "Helping families find the people they've lost — and helping experts bring them answers.",
    "landing.hero_subtitle": "Families share what they know about a missing loved one. Forensic teams share what they've found. EuroFind quietly compares the two — across Europe — to spot possible matches sooner.",
    "landing.cta_family": "I am searching for someone",
    "landing.cta_authority": "I work in forensics or policing",
    "landing.stat.countries": "Countries covered",
    "landing.stat.ante": "Missing-person reports",
    "landing.stat.post": "Unidentified-person records",
    "landing.stat.users": "Verified members",
    "landing.sides_title": "Two sides, one shared goal.",
    "landing.sides_subtitle": "Each side adds what they know, separately. EuroFind compares the details — never names or identities — and flags possible matches for people to check.",
    "landing.family_card_title": "For families",
    "landing.authority_card_title": "For authorities",
    "landing.how_title": "How EuroFind works",
    "landing.browse_btn": "Browse anonymised cases",
    "landing.ways_title": "Three ways to move EuroFind forward.",
    "landing.pos_title": "EuroFind connects what borders, databases and decades have kept apart.",
    "landing.pos_body": "An independent European research platform designed to discover potential links between missing persons and unidentified human remains.",
    "landing.pos_complement": "Complementing official identification systems — never replacing them: potential links are always submitted to the competent authorities for verification.",
    "landing.ways_contribute_title": "Contribute",
    "landing.ways_contribute_body": "Provide eligible case information and expertise as an authorised partner.",
    "landing.ways_contribute_link": "Register as an authority",
    "landing.ways_participate_title": "Participate",
    "landing.ways_participate_body": "Help review and structure case information in your country.",
    "landing.ways_participate_link": "Become a helping hand",
    "landing.ways_fund_title": "Fund",
    "landing.ways_fund_body": "Sponsor the infrastructure, safeguards and growth of the service.",
    "landing.ways_fund_link": "Get in touch with us",
    "landing.footer": "EuroFind is a free, humanitarian tool. It doesn't replace the police or national authorities. In an emergency, or if you have urgent information, please contact your local police first.",
    "landing.trust_title": "Searching for answers, together",
    "landing.trust_body": "If you're here because someone you love is missing, we're so sorry. You don't have to look alone. EuroFind gently brings together what families know and what forensic teams find — with care, and without false hope. It's free, and it's here for you.",
    "landing.family_card_body": "Add a missing-person report — a photo, physical description, what they were last seen wearing, any distinguishing marks. You stay in control of your report and check possible matches privately.",
    "landing.family_b1": "A simple, step-by-step form",
    "landing.family_b2": "Private match suggestions, just for you",
    "landing.family_b3": "Always free",
    "landing.family_link": "Create a family account",
    "landing.authority_card_body": "Add unidentified-person records — estimated age range, gender, identifying features, and where and when they were found. Browse every record and review suggested matches side by side with the missing-person report.",
    "landing.authority_b1": "Structured forensic intake",
    "landing.authority_b2": "A searchable database across borders",
    "landing.authority_b3": "Clear, rule-based match suggestions",
    "landing.authority_link": "Request an authority account",
    "landing.step1_title": "Details go in",
    "landing.step1_desc": "Families and forensic teams add what they know through simple, dedicated forms.",
    "landing.step2_title": "Cases are compared",
    "landing.step2_desc": "A clear, rule-based engine scores possible matches on age, gender, country, physical traits and distinguishing marks.",
    "landing.step3_title": "People decide",
    "landing.step3_desc": "Matches are suggestions, never conclusions. Forensic experts, families and the right authorities make the final call together.",
    "auth.signin_title": "Sign in",
    "auth.signin_sub": "Sign in to see your dashboard and any possible matches.",
    "auth.email": "Email",
    "auth.password": "Password",
    "auth.no_account": "New here?",
    "auth.create_account": "Create an account",
    "auth.already_account": "Already have an account?",
    "auth.signing_in": "Signing in…",
    "auth.creating": "Creating account…",
    "auth.forgot": "Forgot password?",
    "auth.withdrawn_notice": "An administrator has removed your access. Please contact your organisation to find out more.",
    "forgot.title": "Reset your password",
    "forgot.sub": "Enter the email for your account and we will send you a link to set a new password.",
    "forgot.submit": "Send reset link",
    "forgot.sending": "Sending…",
    "forgot.sent_title": "Check your inbox",
    "forgot.sent_sub": "If an account exists for that email, a reset link is on its way. It expires in 1 hour.",
    "forgot.back_to_login": "Back to sign in",
    "reset.title": "Choose a new password",
    "reset.sub": "Enter a new password for your account.",
    "reset.password": "New password",
    "reset.confirm": "Confirm new password",
    "reset.submit": "Update password",
    "reset.updating": "Updating…",
    "reset.success_title": "Password updated",
    "reset.success_sub": "You can now sign in with your new password.",
    "reset.mismatch": "Passwords do not match.",
    "reset.invalid": "This reset link is invalid or has expired. Please request a new one.",
    "reset.no_token": "No reset token found. Please open the link from your email.",
    "reset.go_login": "Go to sign in",
    "auth.register_title": "Create your account",
    "auth.register_sub": "Tell us how you'll be using EuroFind.",
    "auth.iam": "I am a…",
    "auth.role.family": "Family member",
    "auth.role.family_sub": "Searching for a missing loved one",
    "auth.role.authority": "Authority / Forensic expert",
    "auth.role.authority_sub": "Working on unidentified-person cases",
    "auth.full_name": "Full name",
    "auth.organisation": "Organisation",
    "auth.optional": "(optional)",
    "auth.password_hint": "At least 6 characters.",
    "auth.authority_notice": "Authority accounts are checked by an administrator before you can add cases. You can sign in right away — adding records unlocks once you're approved, usually within one business day.",
    "dash.welcome": "Welcome,",
    "dash.workspace.family": "Family workspace",
    "dash.workspace.authority": "Authority workspace",
    "dash.workspace.admin": "Admin workspace",
    "dash.family_sub": "Manage the reports you've submitted and check for possible matches.",
    "dash.authority_sub": "Add and review unidentified-person records, and compare them against missing-person reports.",
    "dash.admin_sub": "Keep an eye on activity and approve professional accounts.",
    "dash.new_ante": "Report a missing person",
    "dash.new_post": "Add unidentified-person record",
    "dash.review_auth": "Review authorities",
    "dash.matches": "Matches",
    "dash.your_reports": "Your missing-person reports",
    "dash.your_post": "Your unidentified-person records",
    "dash.ante_all": "Missing-person reports across Europe",
    "dash.mine_only": "Only mine",
    "dash.all_reports": "All reports",
    "dash.bulk_select": "Bulk delete",
    "dash.bulk_cancel": "Cancel",
    "dash.select_all": "Select all",
    "dash.selected": "selected",
    "dash.delete_selected": "Delete selected",
    "dash.bulk_confirm": "Permanently delete the selected cases? This cannot be undone.",
    "dash.bulk_deleted": "cases deleted",
    "dash.filter_contributor": "Contributor",
    "dash.filter_all_users": "All users",
    "dash.filter_added": "Added",
    "dash.filter_any_time": "Any time",
    "dash.filter_today": "Today",
    "dash.filter_7d": "Last 7 days",
    "dash.filter_30d": "Last 30 days",
    "dash.case_status": "Case status",
    "dash.brand_title": "You are part of the EuroFind network",
    "dash.brand_body": "A humanitarian network connecting families and forensic teams across Europe. Show the EuroFind mark on your own site or materials to let people know you take part.",
    "dash.download_logo": "Download logo",
    "dash.stat_ante_all": "Missing-person reports (all)",
    "dash.stat_post": "Unidentified-person records",
    "dash.stat_matches": "Potential matches",
    "dash.monitored": "Monitored",
    "dash.my_imports": "My imports",
    "dash.bulk_import": "Bulk import",
    "dash.reports_count": "reports",
    "dash.ante_empty": "No missing-person reports yet",
    "dash.ante_empty_sub": "When families add cases, they'll show up here.",
    "dash.no_reports": "No reports yet",
    "dash.no_reports_sub": "Start by adding a missing-person report. What you enter stays private to you and is only compared against unidentified-person records.",
    "dash.no_post": "No unidentified-person records yet",
    "dash.no_post_sub": "Add the details of an unidentified person so they can be compared across borders.",
    "common.delete": "Delete",
    "common.loading": "Loading…",
    "common.sign_out": "Sign out",
    "matches.eyebrow": "Comparison engine",
    "matches.title": "Potential matches",
    "matches.sub": "These are suggestions from a clear, rule-based comparison — not confirmed identifications. Every pairing needs a person to check it. A higher score just means more details line up.",
    "matches.min_score": "Minimum score",
    "matches.empty_title": "No potential matches",
    "matches.empty_sub": "As more missing-person and unidentified-person data comes in, suggestions will show up here.",
    "matches.score": "Match score",
    "matches.circ_hits": "Matching circumstances",
    "review.read_only": "Only the organisations involved in this match (or an admin) can change its status or add notes.",
    "dash.download_logo_transparent": "Transparent logo",
    "imp.eyebrow": "Data import",
    "imp.title.ante": "Bulk import — missing persons",
    "imp.title.post": "Bulk import — unidentified people",
    "imp.tab.ante": "Missing persons",
    "imp.tab.post": "Unidentified people",
    "imp.desc.ante": "Upload a CSV or XLSX of public, GDPR-cleared missing-person records. The template captures the full physical profile so the matching engine can compare against unidentified bodies. Each row automatically receives an EF reference code.",
    "imp.desc.post": "Upload a CSV or XLSX of unidentified-body records (e.g. from an authority, NGO or investigative journalist). Only COUNTRY is required per row; a reference code is generated automatically when left empty.",
    "imp.required.ante": "Columns (only “Full name” and “Last seen - Country” are required — the rest are optional):",
    "imp.required.post": "Columns (only COUNTRY is required — the rest are optional):",
    "imp.template": "Template",
    "imp.drag": "Drag a file here, or",
    "imp.choose_file": "Choose file",
    "imp.importing": "Importing…",
    "imp.import_records": "Import records",
    "imp.pdf.title": "Import from a PDF (AI)",
    "imp.pdf.desc": "Upload a case dossier as PDF (police notice, report or saved web page). The AI reads the text — including scanned documents — extracts the case details and photos, and proposes records you can review before importing.",
    "imp.pdf.analyse": "Read PDF",
    "imp.pdf.hint": "Reading the PDF and extracting case data — this can take up to a minute.",
    "imp.url.title": "Import from a web page (AI)",
    "imp.url.desc": "Paste the address of a published overview page (e.g. a police list). The AI reads it, follows the individual case pages and proposes records you can review before importing. Only use pages your organisation is authorised to reuse.",
    "imp.url.label": "Page URL",
    "imp.url.max": "Max cases",
    "imp.url.analyse": "Analyse page",
    "imp.url.analysing": "Analysing…",
    "imp.url.hint": "Reading the page and its case details — this can take up to a minute.",
    "imp.url.found": "record(s) found. Tick the ones you want to import.",
    "imp.url.select_all": "Select all",
    "imp.url.deselect_all": "Deselect all",
    "imp.url.import_btn": "Import selected",
    "imp.url.empty": "The AI could not extract any records from that page. It may rely on JavaScript to load its data, or use an unusual layout. Try a different overview page, or use the CSV import above.",
    "imp.url.imported": "Imported:",
    "imp.skipped_lc": "skipped",
    "imp.errors_lc": "error(s)",
    "imp.summary": "Import summary",
    "imp.created": "Created",
    "imp.skipped": "Skipped (empty)",
    "imp.errors": "Errors",
    "imp.all_ok": "All valid rows imported successfully.",
    "imp.some_failed": "Some rows could not be imported:",
    "imp.row": "Row",
    "thanks.title": "Thank you — we have received your report",
    "thanks.body1": "Thank you for entrusting us with your report. We know how difficult this step can be.",
    "thanks.body2": "Your information will be compared, confidentially, with data shared by forensic services and authorities across Europe.",
    "thanks.body3": "If there is a relevant development, we — or one of our local liaisons — will contact you personally. You do not need to do anything further.",
    "thanks.body4": "We cannot make any promises about the outcome, but please know that every report strengthens the search.",
    "thanks.back": "Back to my dashboard",
    "matches.ante": "Missing person",
    "matches.post": "Unidentified person",
    "matches.timeline_warning_title": "Timeline conflict — manual review needed",
    "matches.timeline_warning_body": "The recovery date is before the date the person was last seen, so this pairing is physically impossible on paper. It still scores very high on every other detail — a late or inaccurate missing-person report can cause this. Please verify the dates by hand.",
    "matches.date_last_seen": "Last seen",
    "matches.date_found": "Recovered / found",
    "review.status_label": "Status",
    "review.status.new": "New",
    "review.status.investigating": "Under investigation",
    "review.status.excluded": "Ruled out",
    "review.status.confirmed": "Confirmed",
    "review.log_title": "Investigation log",
    "review.no_notes": "No notes yet.",
    "matches.count_label": "match(es)",
    "matches.load_more": "Load more results",
    "matches.top_shown": "Showing top {shown} of {total}",
    "form.contact_name": "Contact person (name)",
    "form.contact_phone": "Contact phone",
    "form.contact_email": "Contact email",
    "review.status_admin_only": "Only the EuroFind administrator can change this status — add a note to share your findings.",
    "imp.type_warn.ante": "Looks like an unidentified body — verify before importing as a missing person",
    "imp.type_warn.post": "Looks like a missing person — verify before importing as an unidentified body",
    "imp.merged": "Merged",
    "imp.merged_note": "Duplicates — the existing case was supplemented instead of creating a new one",
    "detail.merged_from": "Supplemented from",
    "help.title": "International helping hands",
    "help.subtitle": "Help EuroFind grow: collect public cases from your country, fill in our report sheets and upload them for review.",
    "help.apply_tab": "Apply for a code",
    "help.upload_tab": "I already have a code",
    "help.photos": "Photos of the person (optional, max 6)",
    "help.photos_count": "photo(s)",
    "help.thanks_stats": "You have now contributed {total} sheet(s) in total — {approved} approved and live on the platform.",
    "help.kind_corrected": "{n} sheet(s) were automatically moved to the correct category: the sheet type in the PDF did not match your selection.",
    "help.thanks_milestone": "A heartfelt thank you! With {approved} approved sheets you are one of our most valued helping hands.",
    "help.photos_hint": "JPG/PNG — after approval these photos are shown with the case on the platform.",
    "help.photos_single": "Photos can only be added when uploading one sheet at a time, so we know which case they belong to.",
    "help.applied_next": "Once approved you will receive your personal code — then come back here and choose 'I already have a code' to upload your sheets.",
    "help.name": "Full name",
    "help.email": "Email address",
    "help.org": "Organisation (optional)",
    "help.scope": "What would you like to cover?",
    "help.scope_hint": "e.g. 'all unidentified bodies in Bulgaria'",
    "help.apply_btn": "Apply",
    "help.applied": "Thank you! Your application is pending — the EuroFind team will contact you with your personal EuroFind code.",
    "help.status_pending": "Your application is still pending — the EuroFind team will contact you with your personal code.",
    "help.status_approved": "Your application was already approved — use your EuroFind code on the upload tab.",
    "help.status_rejected": "Your application was not approved. Contact the EuroFind team for more information.",
    "help.code": "Your EuroFind code",
    "help.kind": "What are you uploading?",
    "help.kind_am": "Missing persons (green sheet)",
    "help.kind_pm": "Unidentified bodies (blue sheet)",
    "help.templates": "Download the report sheets",
    "help.tpl_am": "Missing person sheet",
    "help.tpl_pm": "Unidentified person sheet",
    "help.files": "Filled PDF sheets (max 10)",
    "help.upload_btn": "Upload to EuroFind",
    "help.uploaded": "Received! Your sheets are waiting for review by the EuroFind team and will appear on the platform after approval.",
    "help.upload_more": "Upload more sheets",
    "help.how_title": "How it works",
    "help.how1": "Apply with your name and email address.",
    "help.how2": "After approval you receive your personal EuroFind code and the report sheets.",
    "help.how3": "Fill in one sheet per case — your EuroFind code at the top — and upload them here with your email + code.",
    "help.how_photos": "Photos are never pasted into the PDF — add them in the upload form instead: each sheet gets its own photo box (max 6), so every photo stays linked to the right case.",
    "help.how4": "The EuroFind team reviews every sheet; approved cases appear on the platform.",
    "help.landing_eyebrow": "For volunteers, students & researchers",
    "help.landing_title": "Help us from anywhere in Europe",
    "help.landing_text": "Take a country under your wing and submit cases with our ready-made report sheets. Every sheet is reviewed before it appears on the platform.",
    "help.landing_cta": "Become a helping hand",
    "kind.ante": "Missing person",
    "kind.post": "Unidentified",
    "notif.helper_apply_msg": "{author} applied as a helping hand — review the application",
    "notif.helper_upload_msg": "{author} uploaded {n} report sheet(s) to the waiting box",
    "detail.back_list": "Back to results",
    "browse.save_search": "Save search",
    "browse.saved": "Saved searches",
    "browse.name_prompt": "Name this search:",
    "browse.saved_ok": "Search saved",
    "browse.load_more": "Load more",
    "form.source_ref": "Source reference",
    "match.new_hint": "A match stays 'New' until an involved organisation sets a status or adds a note.",
    "match.compare": "Side by side",
    "match.compare_title": "Match comparison",
    "match.highmatch_title": "High match — what now?",
    "match.highmatch_body": "Verify both files carefully, contact the organisation holding the other file (see the file's contact details), and record your findings below via the status and notes. The EuroFind administration (BVM) is alerted automatically for matches of 85+ and can coordinate between countries.",
    "notif.title": "Notifications",
    "notif.empty": "No notifications yet.",
    "notif.note_msg": "{author} added a note to match {a} × {b}",
    "notif.status_msg": "{author} set match {a} × {b} to {status}",
    "review.note_placeholder": "Add a note about this match…",
    "review.add_note": "Add note",
    "review.show_details": "Show details",
    "review.hide_details": "Hide",
    "review.hide_excluded": "Hide ruled-out matches",
    "review.updated_by": "Updated by",
    "browse.title": "Browse cases",
    "browse.sub": "Search by name, country, city, physical traits or distinguishing marks.",
    "browse.search": "Search…",
    "browse.tab_post": "Unidentified people",
    "browse.tab_ante": "Missing persons",
    "browse.locked_title": "Sign in to browse cases",
    "browse.locked_sub": "Out of respect and for privacy, only verified accounts can browse cases.",
    "form.section.id": "Identification",
    "form.section.demo": "Demographics",
    "form.section.physical": "Physical description",
    "form.section.last_seen": "Last seen",
    "form.section.found": "Location & date found",
    "form.section.photo": "Photo",
    "form.full_name": "Full name of missing person *",
    "form.ref_code": "Internal reference code (optional)",
    "form.relation": "Your relation to them (optional)",
    "form.age_min": "Estimated age (min)",
    "form.age_max": "Estimated age (max)",
    "form.age": "Age",
    "form.dob": "Date of birth",
    "form.age_at_disappearance": "Age at disappearance",
    "form.age_unknown_dob": "Or enter age manually (if date of birth unknown)",
    "form.height_min": "Height (min, cm)",
    "form.height_max": "Height (max, cm)",
    "form.gender": "Gender",
    "form.gender.male": "Male",
    "form.gender.female": "Female",
    "form.gender.unknown": "Unknown",
    "form.height": "Height (cm)",
    "form.weight": "Weight (kg)",
    "form.hair": "Hair color",
    "form.eye": "Eye color",
    "attr.black": "Black",
    "attr.brown": "Brown",
    "attr.blond": "Blond",
    "attr.red": "Red",
    "attr.grey": "Grey",
    "attr.white": "White",
    "attr.bald": "Bald",
    "attr.blue": "Blue",
    "attr.green": "Green",
    "attr.hazel": "Hazel",
    "attr.amber": "Amber",
    "tag.hair": "hair",
    "tag.eyes": "eyes",
    "form.dob_day": "DD",
    "form.dob_month": "MM",
    "form.dob_year": "YYYY",
    "form.dob_hint": "Only the year is enough if the full date is unknown.",
    "detail.translate": "Translate details",
    "detail.show_original": "Show original",
    "detail.translated_badge": "Automatic translation",
    "detail.translate_error": "Translation failed. Please try again.",
    "detail.photos_unsaved": "Unsaved photo changes",
    "detail.photos_save": "Save changes",
    "detail.photos_cancel": "Cancel",
    "match.field.age": "Age range",
    "match.field.circumstances": "Circumstances",
    "match.field.gender_partial": "Gender unknown (partial)",
    "form.ethnicity": "Ethnicity / origin",
    "form.marks": "Distinguishing marks (scars, tattoos, birthmarks)",
    "form.clothing": "Clothing or accessories",
    "form.country": "Country *",
    "form.location": "City or region",
    "form.date_seen": "Date last seen",
    "form.date_found": "Date found",
    "form.pmi": "Estimated time since death (days)",
    "form.description.ante": "Circumstances and additional notes",
    "form.description.post": "Case description",
    "form.case_notes": "Internal case notes",
    "form.photo_hint": "Use a clear, identifying photograph. Maximum 8 MB. JPG, PNG or WEBP.",
    "form.upload": "Upload photo",
    "form.uploading": "Uploading…",
    "form.submit.ante": "Submit missing-person report",
    "form.submit.post": "Save unidentified-person record",
    "form.saving": "Saving…",
    "form.required_error": "Please complete the highlighted required fields.",
    "new.ante.title": "Report a missing person",
    "new.post.title": "Add an unidentified-person record",
    "new.ante.sub": "Please add as much accurate detail as you can. Every field helps us find meaningful matches.",
    "new.post.sub": "Enter the details of an unidentified-person case. This stays visible only to verified accounts.",
    "dash.new_case": "Add a new record",
    "choose.title": "What would you like to register?",
    "choose.sub": "Choose the type of record first — the form adapts to your choice.",
    "choose.post.title": "Unidentified body",
    "choose.post.desc": "A deceased person whose identity is unknown. No name needed — you fill in a post-mortem form with a reference code.",
    "choose.ante.title": "Missing person",
    "choose.ante.desc": "A person reported missing. You fill in an ante-mortem form with their name and details.",
    "choose.single": "Fill in one record",
    "choose.bulk": "Bulk import (CSV / URL)",
    "admin.eyebrow": "Admin · vetting",
    "admin.title": "Authority requests",
    "admin.sub": "Review professional accounts asking for access. Only approved authorities can add or edit unidentified-person records.",
    "admin.pending": "Pending",
    "admin.approved": "Approved",
    "admin.rejected": "Rejected",
    "admin.approve": "Approve",
    "admin.reject": "Reject",
    "admin.empty": "Nothing in this list.",
    "invite.panel.title": "Invitation links",
    "invite.panel.sub": "Create one-time, pre-approved links for colleagues you trust. Anyone who signs up through the link becomes an active authority straight away — no extra review needed.",
    "invite.new": "New invitation link",
    "invite.create_title": "Create invitation link",
    "invite.restrict_email": "Restrict to email (optional)",
    "invite.restrict_hint": "If set, only this address can use the link.",
    "invite.expires": "Expires in (days)",
    "invite.note": "Internal note (optional)",
    "invite.create_btn": "Create link",
    "invite.creating": "Creating…",
    "invite.created_title": "Invitation link created",
    "invite.created_sub": "Share this link with your colleague. It is single-use and expires on",
    "invite.copy": "Copy link",
    "invite.copied": "Copied",
    "invite.revoke": "Revoke",
    "invite.done": "Done",
    "invite.page_badge": "Pre-approved authority invitation",
    "invite.page_title": "Set up your authority account",
    "invite.page_sub": "Your account will be activated automatically upon registration.",
    "invite.locked_hint": "This invitation is locked to this email.",
    "invite.submit": "Activate authority account",
    "invite.invalid_title": "Invitation unavailable",
    "invite.back_home": "Back to home",
    "invite.verifying": "Verifying invitation…",
    "pending.title": "Your authority account is pending review",
    "pending.rejected_title": "Account not approved",
    "pending.sub": "Thanks for registering. An administrator will check your professional credentials before giving you access to add cases and see matches. This usually takes about one business day.",
    "pending.rejected_sub": "An administrator has reviewed your request and couldn't approve your authority account. If you think this is a mistake, please contact the EuroFind team with more details.",
    "family.status.title": "Your case is being monitored",
    "family.status.body": "Your report is quietly and continuously compared against records from across Europe. To protect you from distress, we don't show comparison details here. If something relevant comes up, your liaison will contact you directly.",
    "family.status.contact_title": "Your point of contact",
    "family.status.contact_sub": "For any question about your report, please reach out to the liaison for your region.",
    "family.status.no_liaison": "We're assigning a liaison for your region. In the meantime, please contact your local police or the authority where you filed your report.",
    "edit.ante.title": "Edit missing-person report",
    "edit.post.title": "Edit unidentified-person record",
    "edit.sub": "Update any field below. The reference code stays the same.",
    "form.submit.save": "Save changes",
    "common.edit": "Edit",
    "detail.eyebrow": "Case detail",
    "detail.back": "Back to dashboard",
    "detail.privacy_title": "These details are kept private",
    "detail.privacy_body": "To protect families from false hope and distress, we don't show the details of unidentified-person records here. If there's a relevant development on your case, your liaison will contact you directly.",
    "detail.view": "View",
    "detail.imported": "Imported record",
    "detail.source": "Source",
    "detail.reported_by": "Your relation",
    "case.submitted_by": "Submitted by",
    "detail.privacy_cta": "View your case status",
    "detail.copy_link": "Copy link",
    "detail.copied": "Link copied",
    "detail.print": "Print / PDF",
    "detail.export_pdf": "Export PDF",
    "detail.pdf_error": "Could not generate the PDF.",
    "detail.photos_saved": "Photos updated.",
    "detail.photos_error": "Could not update photos.",
    "detail.photo_set_main": "Set as main",
    "detail.photo_remove": "Remove photo",
    "detail.photo_add": "Add photo",
  },
  fr: {
    "nav.home": "Accueil",
    "nav.dashboard": "Tableau de bord",
    "nav.matches": "Correspondances",
    "nav.browse": "Parcourir les cas",
    "nav.admin": "Administration",
    "nav.signin": "Se connecter",
    "nav.signout": "Se déconnecter",
    "nav.create_account": "Créer un compte",
    "landing.eyebrow": "Personnes disparues et non identifiées en Europe",
    "landing.hero_title": "Aider les familles à retrouver ceux qu'elles ont perdus — et aider les experts à leur apporter des réponses.",
    "landing.hero_subtitle": "Les familles partagent ce qu'elles savent d'un proche disparu. Les équipes médico-légales partagent ce qu'elles ont trouvé. EuroFind compare discrètement les deux — à travers l'Europe — pour repérer plus tôt d'éventuelles correspondances.",
    "landing.cta_family": "Je cherche quelqu'un",
    "landing.cta_authority": "Je travaille dans la police ou la médecine légale",
    "landing.stat.countries": "Pays couverts",
    "landing.stat.ante": "Signalements de personnes disparues",
    "landing.stat.post": "Fiches de personnes non identifiées",
    "landing.stat.users": "Membres vérifiés",
    "landing.sides_title": "Deux côtés, un objectif commun.",
    "landing.sides_subtitle": "Chaque côté ajoute ce qu'il sait, séparément. EuroFind compare les détails — jamais les noms ni les identités — et signale d'éventuelles correspondances à vérifier par des personnes.",
    "landing.family_card_title": "Pour les familles",
    "landing.authority_card_title": "Pour les autorités",
    "landing.how_title": "Comment EuroFind fonctionne",
    "landing.browse_btn": "Parcourir les fiches anonymisées",
    "landing.ways_title": "Trois façons de faire avancer EuroFind.",
    "landing.pos_title": "EuroFind relie ce que les frontières, les bases de données et les décennies ont séparé.",
    "landing.pos_body": "Une plateforme de recherche européenne indépendante, conçue pour découvrir des liens potentiels entre personnes disparues et restes humains non identifiés.",
    "landing.pos_complement": "En complément des systèmes d'identification officiels — jamais à leur place : les liens potentiels sont toujours soumis aux autorités compétentes pour vérification.",
    "landing.ways_contribute_title": "Contribuer",
    "landing.ways_contribute_body": "Fournissez des informations de dossiers éligibles et votre expertise en tant que partenaire autorisé.",
    "landing.ways_contribute_link": "S'inscrire comme autorité",
    "landing.ways_participate_title": "Participer",
    "landing.ways_participate_body": "Aidez à vérifier et structurer les informations de dossiers dans votre pays.",
    "landing.ways_participate_link": "Devenir une main tendue",
    "landing.ways_fund_title": "Financer",
    "landing.ways_fund_body": "Parrainez l'infrastructure, les garanties et la croissance du service.",
    "landing.ways_fund_link": "Contactez-nous",
    "landing.footer": "EuroFind est un outil humanitaire gratuit. Il ne remplace pas la police ni les autorités nationales. En cas d'urgence, ou si vous avez des informations urgentes, contactez d'abord votre police locale.",
    "landing.trust_title": "Chercher des réponses, ensemble",
    "landing.trust_body": "Si vous êtes ici parce qu'un proche a disparu, nous sommes profondément désolés. Vous n'avez pas à chercher seul·e. EuroFind rapproche avec douceur ce que savent les familles et ce que trouvent les équipes médico-légales — avec soin, et sans faux espoir. C'est gratuit, et c'est là pour vous.",
    "landing.family_card_body": "Ajoutez un signalement de personne disparue — une photo, une description physique, les vêtements portés la dernière fois, des signes particuliers. Vous gardez le contrôle de votre signalement et consultez les correspondances possibles en privé.",
    "landing.family_b1": "Un formulaire simple, étape par étape",
    "landing.family_b2": "Des suggestions de correspondances privées, rien que pour vous",
    "landing.family_b3": "Toujours gratuit",
    "landing.family_link": "Créer un compte famille",
    "landing.authority_card_body": "Ajoutez des fiches de personnes non identifiées — tranche d'âge estimée, sexe, signes distinctifs, lieu et date de découverte. Parcourez toutes les fiches et examinez les correspondances suggérées côte à côte avec le signalement de disparition.",
    "landing.authority_b1": "Saisie médico-légale structurée",
    "landing.authority_b2": "Une base de données consultable au-delà des frontières",
    "landing.authority_b3": "Des suggestions de correspondance claires, basées sur des règles",
    "landing.authority_link": "Demander un compte autorité",
    "landing.step1_title": "Les informations arrivent",
    "landing.step1_desc": "Les familles et les équipes médico-légales ajoutent ce qu'elles savent via des formulaires simples et dédiés.",
    "landing.step2_title": "Les cas sont comparés",
    "landing.step2_desc": "Un moteur clair, basé sur des règles, évalue les correspondances possibles selon l'âge, le sexe, le pays, les traits physiques et les signes distinctifs.",
    "landing.step3_title": "Des personnes décident",
    "landing.step3_desc": "Les correspondances sont des suggestions, jamais des conclusions. Les experts médico-légaux, les familles et les autorités compétentes tranchent ensemble.",
    "auth.signin_title": "Se connecter",
    "auth.signin_sub": "Accédez à votre tableau de bord et à l'espace correspondances.",
    "auth.email": "Email",
    "auth.password": "Mot de passe",
    "auth.no_account": "Nouveau ici ?",
    "auth.create_account": "Créer un compte",
    "auth.already_account": "Vous avez déjà un compte ?",
    "auth.signing_in": "Connexion…",
    "auth.creating": "Création du compte…",
    "auth.forgot": "Mot de passe oublié ?",
    "auth.withdrawn_notice": "Votre accès a été retiré par un administrateur. Veuillez contacter votre organisation pour plus de détails.",
    "forgot.title": "Réinitialiser votre mot de passe",
    "forgot.sub": "Saisissez l'email de votre compte et nous vous enverrons un lien pour définir un nouveau mot de passe.",
    "forgot.submit": "Envoyer le lien",
    "forgot.sending": "Envoi…",
    "forgot.sent_title": "Vérifiez votre boîte de réception",
    "forgot.sent_sub": "Si un compte existe pour cet email, un lien de réinitialisation est en route. Il expire dans 1 heure.",
    "forgot.back_to_login": "Retour à la connexion",
    "reset.title": "Choisissez un nouveau mot de passe",
    "reset.sub": "Saisissez un nouveau mot de passe pour votre compte.",
    "reset.password": "Nouveau mot de passe",
    "reset.confirm": "Confirmer le mot de passe",
    "reset.submit": "Mettre à jour le mot de passe",
    "reset.updating": "Mise à jour…",
    "reset.success_title": "Mot de passe mis à jour",
    "reset.success_sub": "Vous pouvez maintenant vous connecter avec votre nouveau mot de passe.",
    "reset.mismatch": "Les mots de passe ne correspondent pas.",
    "reset.invalid": "Ce lien de réinitialisation est invalide ou a expiré. Veuillez en demander un nouveau.",
    "reset.no_token": "Aucun jeton de réinitialisation trouvé. Veuillez ouvrir le lien depuis votre email.",
    "reset.go_login": "Aller à la connexion",
    "auth.register_title": "Créer votre compte",
    "auth.register_sub": "Choisissez votre profil d'utilisation d'EuroFind.",
    "auth.iam": "Je suis…",
    "auth.role.family": "Membre d'une famille",
    "auth.role.family_sub": "À la recherche d'un proche disparu",
    "auth.role.authority": "Autorité / Expert médico-légal",
    "auth.role.authority_sub": "Travail sur des affaires de corps non identifiés",
    "auth.full_name": "Nom complet",
    "auth.organisation": "Organisation",
    "auth.optional": "(facultatif)",
    "auth.password_hint": "Au moins 6 caractères.",
    "auth.authority_notice": "Les comptes autorité sont vérifiés par un administrateur avant que vous puissiez ajouter des dossiers. Vous pouvez vous connecter tout de suite — l'ajout de fiches est débloqué une fois votre compte approuvé, généralement sous un jour ouvrable.",
    "dash.welcome": "Bienvenue,",
    "dash.workspace.family": "Espace famille",
    "dash.workspace.authority": "Espace autorité",
    "dash.workspace.admin": "Espace administrateur",
    "dash.family_sub": "Gérez les signalements que vous avez soumis et vérifiez les correspondances possibles.",
    "dash.authority_sub": "Ajoutez et examinez les fiches de personnes non identifiées, et comparez-les aux signalements de personnes disparues.",
    "dash.admin_sub": "Examinez l'activité de la plateforme et approuvez les comptes professionnels.",
    "dash.new_ante": "Signaler une personne disparue",
    "dash.new_post": "Ajouter une fiche de personne non identifiée",
    "dash.review_auth": "Examiner les autorités",
    "dash.matches": "Correspondances",
    "dash.your_reports": "Vos signalements de personnes disparues",
    "dash.your_post": "Vos fiches de personnes non identifiées",
    "dash.ante_all": "Signalements de personnes disparues en Europe",
    "dash.mine_only": "Uniquement les miens",
    "dash.all_reports": "Tous les signalements",
    "dash.bulk_select": "Suppression groupée",
    "dash.bulk_cancel": "Annuler",
    "dash.select_all": "Tout sélectionner",
    "dash.selected": "sélectionné(s)",
    "dash.delete_selected": "Supprimer la sélection",
    "dash.bulk_confirm": "Supprimer définitivement les dossiers sélectionnés ? Cette action est irréversible.",
    "dash.bulk_deleted": "dossiers supprimés",
    "dash.filter_contributor": "Contributeur",
    "dash.filter_all_users": "Tous les utilisateurs",
    "dash.filter_added": "Ajouté",
    "dash.filter_any_time": "Toute période",
    "dash.filter_today": "Aujourd'hui",
    "dash.filter_7d": "7 derniers jours",
    "dash.filter_30d": "30 derniers jours",
    "dash.case_status": "Suivi du dossier",
    "dash.brand_title": "Vous faites partie du réseau EuroFind",
    "dash.brand_body": "Un réseau humanitaire qui relie les familles et les équipes médico-légales à travers l'Europe. Affichez la marque EuroFind sur votre site ou vos supports pour montrer votre participation.",
    "dash.download_logo": "Télécharger le logo",
    "dash.stat_ante_all": "Signalements de personnes disparues (tous)",
    "dash.stat_post": "Fiches de personnes non identifiées",
    "dash.stat_matches": "Correspondances potentielles",
    "dash.monitored": "Surveillé",
    "dash.my_imports": "Mes imports",
    "dash.bulk_import": "Import en masse",
    "dash.reports_count": "signalements",
    "dash.ante_empty": "Aucun signalement de personne disparue pour l'instant",
    "dash.ante_empty_sub": "Lorsque des familles ajoutent des cas, ils apparaîtront ici.",
    "dash.no_reports": "Aucun signalement pour l'instant",
    "dash.no_reports_sub": "Commencez par ajouter un signalement de personne disparue. Vos informations restent privées et ne sont comparées qu'aux fiches de personnes non identifiées.",
    "dash.no_post": "Aucune fiche de personne non identifiée pour l'instant",
    "dash.no_post_sub": "Ajoutez les détails d'une personne non identifiée afin de permettre la comparaison transfrontalière.",
    "common.delete": "Supprimer",
    "common.loading": "Chargement…",
    "common.sign_out": "Se déconnecter",
    "matches.eyebrow": "Moteur de comparaison",
    "matches.title": "Correspondances potentielles",
    "matches.sub": "Ce sont des suggestions issues d'une comparaison claire, basée sur des règles — pas des identifications confirmées. Chaque rapprochement doit être vérifié par une personne. Un score plus élevé signifie simplement que davantage de détails concordent.",
    "matches.min_score": "Score minimum",
    "matches.empty_title": "Aucune correspondance potentielle",
    "matches.empty_sub": "À mesure que davantage de données sur les personnes disparues et non identifiées arrivent, des suggestions apparaîtront ici.",
    "matches.score": "Score de correspondance",
    "matches.circ_hits": "Circonstances concordantes",
    "review.read_only": "Seules les organisations concernées par cette correspondance (ou un administrateur) peuvent modifier son statut ou ajouter des notes.",
    "dash.download_logo_transparent": "Logo transparent",
    "imp.eyebrow": "Import de données",
    "imp.title.ante": "Import groupé — personnes disparues",
    "imp.title.post": "Import groupé — personnes non identifiées",
    "imp.tab.ante": "Personnes disparues",
    "imp.tab.post": "Personnes non identifiées",
    "imp.desc.ante": "Téléversez un fichier CSV ou XLSX de dossiers publics de personnes disparues (conformes au RGPD). Le modèle couvre le profil physique complet afin que le moteur de correspondance puisse comparer avec les corps non identifiés. Chaque ligne reçoit automatiquement un code de référence EF.",
    "imp.desc.post": "Téléversez un fichier CSV ou XLSX de dossiers de corps non identifiés (p. ex. d'une autorité, d'une ONG ou d'un journaliste d'investigation). Seul COUNTRY est obligatoire par ligne ; un code de référence est généré automatiquement s'il est laissé vide.",
    "imp.required.ante": "Colonnes (seuls « Full name » et « Last seen - Country » sont obligatoires — le reste est facultatif) :",
    "imp.required.post": "Colonnes (seul COUNTRY est obligatoire — le reste est facultatif) :",
    "imp.template": "Modèle",
    "imp.drag": "Glissez un fichier ici, ou",
    "imp.choose_file": "Choisir un fichier",
    "imp.importing": "Import en cours…",
    "imp.import_records": "Importer les fiches",
    "imp.pdf.title": "Importer depuis un PDF (IA)",
    "imp.pdf.desc": "Téléversez un dossier au format PDF (avis de police, rapport ou page web enregistrée). L'IA lit le texte — y compris les documents scannés —, extrait les détails et les photos, et propose des fiches à vérifier avant l'import.",
    "imp.pdf.analyse": "Analyser le PDF",
    "imp.pdf.hint": "Lecture du PDF et extraction des données — cela peut prendre jusqu'à une minute.",
    "imp.url.title": "Importer depuis une page web (IA)",
    "imp.url.desc": "Collez l'adresse d'une page de synthèse publiée (p. ex. une liste de la police). L'IA la lit, suit les pages de chaque cas et propose des fiches que vous pouvez vérifier avant l'import. N'utilisez que des pages que votre organisation est autorisée à réutiliser.",
    "imp.url.label": "URL de la page",
    "imp.url.max": "Cas max.",
    "imp.url.analyse": "Analyser la page",
    "imp.url.analysing": "Analyse en cours…",
    "imp.url.hint": "Lecture de la page et des détails des cas — cela peut prendre jusqu'à une minute.",
    "imp.url.found": "fiche(s) trouvée(s). Cochez celles que vous souhaitez importer.",
    "imp.url.select_all": "Tout sélectionner",
    "imp.url.deselect_all": "Tout désélectionner",
    "imp.url.import_btn": "Importer la sélection",
    "imp.url.empty": "L'IA n'a pu extraire aucune fiche de cette page. Elle repose peut-être sur JavaScript pour charger ses données, ou utilise une mise en page inhabituelle. Essayez une autre page de synthèse, ou utilisez l'import CSV ci-dessus.",
    "imp.url.imported": "Importées :",
    "imp.skipped_lc": "ignorée(s)",
    "imp.errors_lc": "erreur(s)",
    "imp.summary": "Résumé de l'import",
    "imp.created": "Créées",
    "imp.skipped": "Ignorées (vides)",
    "imp.errors": "Erreurs",
    "imp.all_ok": "Toutes les lignes valides ont été importées avec succès.",
    "imp.some_failed": "Certaines lignes n'ont pas pu être importées :",
    "imp.row": "Ligne",
    "thanks.title": "Merci — nous avons bien reçu votre signalement",
    "thanks.body1": "Merci de nous avoir confié votre signalement. Nous savons combien cette démarche peut être difficile.",
    "thanks.body2": "Vos informations seront comparées, en toute confidentialité, avec les données partagées par les services médico-légaux et les autorités à travers l'Europe.",
    "thanks.body3": "En cas d'évolution pertinente, nous — ou l'un de nos agents de liaison locaux — vous contacterons personnellement. Vous n'avez rien d'autre à faire.",
    "thanks.body4": "Nous ne pouvons rien promettre quant au résultat, mais sachez que chaque signalement renforce les recherches.",
    "thanks.back": "Retour à mon tableau de bord",
    "matches.ante": "Personne disparue",
    "matches.post": "Personne non identifiée",
    "matches.timeline_warning_title": "Conflit de chronologie — vérification manuelle requise",
    "matches.timeline_warning_body": "La date de découverte est antérieure à la date de la dernière apparition de la personne : ce rapprochement est donc théoriquement impossible. Il obtient pourtant un score très élevé sur tous les autres détails — un signalement de disparition tardif ou inexact peut en être la cause. Veuillez vérifier les dates manuellement.",
    "matches.date_last_seen": "Vu(e) pour la dernière fois",
    "matches.date_found": "Découvert(e)",
    "review.status_label": "Statut",
    "review.status.new": "Nouveau",
    "review.status.investigating": "En cours d'enquête",
    "review.status.excluded": "Écarté",
    "review.status.confirmed": "Confirmé",
    "review.log_title": "Journal d'enquête",
    "review.no_notes": "Aucune note pour l'instant.",
    "matches.count_label": "correspondance(s)",
    "matches.load_more": "Charger plus de résultats",
    "matches.top_shown": "Affichage des {shown} meilleures sur {total}",
    "form.contact_name": "Personne de contact (nom)",
    "form.contact_phone": "Téléphone de contact",
    "form.contact_email": "E-mail de contact",
    "review.status_admin_only": "Seul l'administrateur EuroFind peut modifier ce statut — ajoutez une note pour partager vos constatations.",
    "imp.type_warn.ante": "Ressemble à un corps non identifié — vérifiez avant d'importer comme personne disparue",
    "imp.type_warn.post": "Ressemble à une personne disparue — vérifiez avant d'importer comme corps non identifié",
    "imp.merged": "Fusionnés",
    "imp.merged_note": "Doublons — le dossier existant a été complété au lieu d'en créer un nouveau",
    "detail.merged_from": "Complété depuis",
    "help.title": "Mains tendues internationales",
    "help.subtitle": "Aidez EuroFind à grandir : rassemblez des cas publics de votre pays, remplissez nos fiches et téléversez-les pour vérification.",
    "help.apply_tab": "Demander un code",
    "help.upload_tab": "J'ai déjà un code",
    "help.photos": "Photos de la personne (optionnel, max 6)",
    "help.photos_count": "photo(s)",
    "help.thanks_stats": "Vous avez maintenant contribué {total} fiche(s) au total — {approved} approuvée(s) et en ligne sur la plateforme.",
    "help.kind_corrected": "{n} fiche(s) ont été déplacées automatiquement vers la bonne catégorie : le type de fiche du PDF ne correspondait pas à votre sélection.",
    "help.thanks_milestone": "Un grand merci ! Avec {approved} fiches approuvées, vous êtes l'une de nos aides les plus précieuses.",
    "help.photos_hint": "JPG/PNG — après approbation, ces photos sont affichées avec le dossier sur la plateforme.",
    "help.photos_single": "Les photos ne peuvent être ajoutées que lors du téléversement d'une seule fiche à la fois, afin de savoir à quel dossier elles appartiennent.",
    "help.applied_next": "Une fois approuvé, vous recevrez votre code personnel — revenez ensuite ici et choisissez « J'ai déjà un code » pour téléverser vos fiches.",
    "help.name": "Nom complet",
    "help.email": "Adresse e-mail",
    "help.org": "Organisation (facultatif)",
    "help.scope": "Que souhaitez-vous couvrir ?",
    "help.scope_hint": "p.ex. « tous les corps non identifiés en Bulgarie »",
    "help.apply_btn": "Postuler",
    "help.applied": "Merci ! Votre demande est en attente — l'équipe EuroFind vous contactera avec votre code EuroFind personnel.",
    "help.status_pending": "Votre demande est encore en attente — l'équipe EuroFind vous contactera avec votre code personnel.",
    "help.status_approved": "Votre demande a déjà été approuvée — utilisez votre code EuroFind dans l'onglet de téléversement.",
    "help.status_rejected": "Votre demande n'a pas été approuvée. Contactez l'équipe EuroFind pour plus d'informations.",
    "help.code": "Votre code EuroFind",
    "help.kind": "Que téléversez-vous ?",
    "help.kind_am": "Personnes disparues (fiche verte)",
    "help.kind_pm": "Corps non identifiés (fiche bleue)",
    "help.templates": "Télécharger les fiches",
    "help.tpl_am": "Fiche personne disparue",
    "help.tpl_pm": "Fiche corps non identifié",
    "help.files": "Fiches PDF remplies (max 10)",
    "help.upload_btn": "Téléverser vers EuroFind",
    "help.uploaded": "Bien reçu ! Vos fiches attendent la vérification de l'équipe EuroFind et apparaîtront sur la plateforme après approbation.",
    "help.upload_more": "Téléverser d'autres fiches",
    "help.how_title": "Comment ça marche",
    "help.how1": "Postulez avec votre nom et votre adresse e-mail.",
    "help.how2": "Après approbation, vous recevez votre code EuroFind personnel et les fiches.",
    "help.how3": "Remplissez une fiche par cas — votre code EuroFind en haut — et téléversez-les ici avec votre e-mail + code.",
    "help.how_photos": "Ne collez jamais de photos dans le PDF — ajoutez-les dans le formulaire de téléversement : chaque fiche a sa propre case photos (max 6), ainsi chaque photo reste liée au bon dossier.",
    "help.how4": "L'équipe EuroFind vérifie chaque fiche ; les cas approuvés apparaissent sur la plateforme.",
    "help.landing_eyebrow": "Pour bénévoles, étudiants & chercheurs",
    "help.landing_title": "Aidez-nous depuis partout en Europe",
    "help.landing_text": "Prenez un pays sous votre aile et soumettez des cas avec nos fiches prêtes à l'emploi. Chaque fiche est vérifiée avant d'apparaître sur la plateforme.",
    "help.landing_cta": "Devenir une main tendue",
    "kind.ante": "Personne disparue",
    "kind.post": "Non identifié",
    "notif.helper_apply_msg": "{author} s'est porté(e) candidat(e) comme main tendue — vérifiez la candidature",
    "notif.helper_upload_msg": "{author} a téléversé {n} fiche(s) dans la boîte d'attente",
    "detail.back_list": "Retour aux résultats",
    "browse.save_search": "Enregistrer la recherche",
    "browse.saved": "Recherches enregistrées",
    "browse.name_prompt": "Nommez cette recherche :",
    "browse.saved_ok": "Recherche enregistrée",
    "browse.load_more": "Charger plus",
    "form.source_ref": "Référence source",
    "match.new_hint": "Un match reste « Nouveau » jusqu'à ce qu'une organisation impliquée définisse un statut ou ajoute une note.",
    "match.compare": "Côte à côte",
    "match.compare_title": "Comparaison du match",
    "match.highmatch_title": "Match élevé — et maintenant ?",
    "match.highmatch_body": "Vérifiez soigneusement les deux fiches, contactez l'organisation qui détient l'autre fiche (voir ses coordonnées) et consignez vos constats ci-dessous via le statut et les notes. L'administration EuroFind (BVM) est alertée automatiquement pour les matchs de 85+ et peut coordonner entre pays.",
    "notif.title": "Notifications",
    "notif.empty": "Aucune notification pour l'instant.",
    "notif.note_msg": "{author} a ajouté une note au match {a} × {b}",
    "notif.status_msg": "{author} a défini le match {a} × {b} sur {status}",
    "review.note_placeholder": "Ajouter une note sur cette correspondance…",
    "review.add_note": "Ajouter une note",
    "review.show_details": "Afficher les détails",
    "review.hide_details": "Masquer",
    "review.hide_excluded": "Masquer les correspondances écartées",
    "review.updated_by": "Mis à jour par",
    "browse.title": "Parcourir les cas",
    "browse.sub": "Recherchez par nom, pays, ville, caractéristiques physiques ou signes distinctifs.",
    "browse.search": "Rechercher…",
    "browse.tab_post": "Personnes non identifiées",
    "browse.tab_ante": "Personnes disparues",
    "browse.locked_title": "Connectez-vous pour parcourir les cas",
    "browse.locked_sub": "Par respect et pour la confidentialité, la navigation est réservée aux comptes vérifiés.",
    "form.section.id": "Identification",
    "form.section.demo": "Démographie",
    "form.section.physical": "Description physique",
    "form.section.last_seen": "Dernière apparition",
    "form.section.found": "Lieu et date de découverte",
    "form.section.photo": "Photo",
    "form.full_name": "Nom complet de la personne disparue *",
    "form.ref_code": "Code de référence interne (facultatif)",
    "form.relation": "Votre lien avec la personne (facultatif)",
    "form.age_min": "Âge estimé (min)",
    "form.age_max": "Âge estimé (max)",
    "form.age": "Âge",
    "form.dob": "Date de naissance",
    "form.age_at_disappearance": "Âge à la disparition",
    "form.age_unknown_dob": "Ou saisir l'âge manuellement (si date de naissance inconnue)",
    "form.height_min": "Taille (min, cm)",
    "form.height_max": "Taille (max, cm)",
    "form.gender": "Sexe",
    "form.gender.male": "Homme",
    "form.gender.female": "Femme",
    "form.gender.unknown": "Inconnu",
    "form.height": "Taille (cm)",
    "form.weight": "Poids (kg)",
    "form.hair": "Couleur des cheveux",
    "form.eye": "Couleur des yeux",
    "attr.black": "Noir",
    "attr.brown": "Brun",
    "attr.blond": "Blond",
    "attr.red": "Roux",
    "attr.grey": "Gris",
    "attr.white": "Blanc",
    "attr.bald": "Chauve",
    "attr.blue": "Bleu",
    "attr.green": "Vert",
    "attr.hazel": "Noisette",
    "attr.amber": "Ambre",
    "tag.hair": "cheveux",
    "tag.eyes": "yeux",
    "form.dob_day": "JJ",
    "form.dob_month": "MM",
    "form.dob_year": "AAAA",
    "form.dob_hint": "L'année seule suffit si la date complète est inconnue.",
    "detail.translate": "Traduire les détails",
    "detail.show_original": "Afficher l'original",
    "detail.translated_badge": "Traduction automatique",
    "detail.translate_error": "Échec de la traduction. Veuillez réessayer.",
    "detail.photos_unsaved": "Photos non enregistrées",
    "detail.photos_save": "Enregistrer",
    "detail.photos_cancel": "Annuler",
    "match.field.age": "Tranche d'âge",
    "match.field.circumstances": "Circonstances",
    "match.field.gender_partial": "Sexe inconnu (partiel)",
    "form.ethnicity": "Origine ethnique",
    "form.marks": "Signes distinctifs (cicatrices, tatouages, marques de naissance)",
    "form.clothing": "Vêtements ou accessoires",
    "form.country": "Pays *",
    "form.location": "Ville ou région",
    "form.date_seen": "Date de la dernière apparition",
    "form.date_found": "Date de la découverte",
    "form.pmi": "Temps écoulé depuis le décès (jours, estimé)",
    "form.description.ante": "Circonstances et notes complémentaires",
    "form.description.post": "Description du cas",
    "form.case_notes": "Notes internes",
    "form.photo_hint": "Utilisez une photographie claire et identifiable. Maximum 8 Mo. JPG, PNG ou WEBP.",
    "form.upload": "Télécharger une photo",
    "form.uploading": "Téléchargement…",
    "form.submit.ante": "Soumettre le signalement",
    "form.submit.post": "Enregistrer la fiche",
    "form.saving": "Enregistrement…",
    "form.required_error": "Veuillez compléter les champs obligatoires surlignés.",
    "new.ante.title": "Signaler une personne disparue",
    "new.post.title": "Ajouter une fiche de personne non identifiée",
    "new.ante.sub": "Veuillez fournir le plus de détails précis possible. Chaque champ aide le moteur à proposer des candidats pertinents.",
    "new.post.sub": "Saisissez les données structurées du cas. Les informations restent accessibles uniquement aux comptes vérifiés.",
    "dash.new_case": "Ajouter une fiche",
    "choose.title": "Que souhaitez-vous enregistrer ?",
    "choose.sub": "Choisissez d'abord le type de fiche — le formulaire s'adapte à votre choix.",
    "choose.post.title": "Personne non identifiée",
    "choose.post.desc": "Une personne décédée dont l'identité est inconnue. Aucun nom requis — vous remplissez une fiche post-mortem avec un code de référence.",
    "choose.ante.title": "Personne disparue",
    "choose.ante.desc": "Une personne signalée disparue. Vous remplissez une fiche ante-mortem avec son nom et ses détails.",
    "choose.single": "Remplir une fiche",
    "choose.bulk": "Import groupé (CSV / URL)",
    "admin.eyebrow": "Administration · vérification",
    "admin.title": "Demandes d'accès autorité",
    "admin.sub": "Examinez les comptes professionnels qui demandent l'accès. Seules les autorités approuvées peuvent ajouter ou modifier des fiches de personnes non identifiées.",
    "admin.pending": "En attente",
    "admin.approved": "Approuvés",
    "admin.rejected": "Rejetés",
    "admin.approve": "Approuver",
    "admin.reject": "Rejeter",
    "admin.empty": "Rien dans cette liste.",
    "invite.panel.title": "Liens d'invitation",
    "invite.panel.sub": "Générez des liens à usage unique pré-approuvés et envoyez-les à des collègues de confiance. Toute personne s'inscrivant via le lien devient autorité active immédiatement — aucune révision admin nécessaire.",
    "invite.new": "Nouveau lien d'invitation",
    "invite.create_title": "Créer un lien d'invitation",
    "invite.restrict_email": "Restreindre à un email (facultatif)",
    "invite.restrict_hint": "Si défini, seule cette adresse peut utiliser le lien.",
    "invite.expires": "Expire dans (jours)",
    "invite.note": "Note interne (facultatif)",
    "invite.create_btn": "Créer le lien",
    "invite.creating": "Création…",
    "invite.created_title": "Lien d'invitation créé",
    "invite.created_sub": "Partagez ce lien avec votre collègue. Il est à usage unique et expire le",
    "invite.copy": "Copier le lien",
    "invite.copied": "Copié",
    "invite.revoke": "Révoquer",
    "invite.done": "Terminer",
    "invite.page_badge": "Invitation pré-approuvée",
    "invite.page_title": "Configurez votre compte autorité",
    "invite.page_sub": "Votre compte sera activé automatiquement à l'inscription.",
    "invite.locked_hint": "Cette invitation est verrouillée sur cet email.",
    "invite.submit": "Activer le compte autorité",
    "invite.invalid_title": "Invitation indisponible",
    "invite.back_home": "Retour à l'accueil",
    "invite.verifying": "Vérification de l'invitation…",
    "pending.title": "Votre compte autorité est en cours d'examen",
    "pending.rejected_title": "Compte non approuvé",
    "pending.sub": "Merci pour votre inscription. Un administrateur vérifiera vos références professionnelles avant de vous donner accès à l'ajout de dossiers et aux correspondances. Cela prend généralement environ un jour ouvrable.",
    "pending.rejected_sub": "Un administrateur a examiné votre demande et n'a pas pu approuver votre compte autorité. Si vous estimez qu'il s'agit d'une erreur, veuillez contacter les administrateurs d'EuroFind avec des justificatifs.",
    "family.status.title": "Votre dossier est surveillé",
    "family.status.body": "Votre signalement est comparé en continu et en toute confidentialité aux dossiers détenus à travers l'Europe. Pour vous épargner toute détresse, nous n'affichons pas ici les détails des comparaisons. Votre référent vous contactera directement si un élément pertinent est trouvé.",
    "family.status.contact_title": "Votre interlocuteur",
    "family.status.contact_sub": "Pour toute question concernant votre signalement, veuillez contacter le référent de votre région.",
    "family.status.no_liaison": "Un référent dédié à votre région est en cours d'attribution. En attendant, veuillez contacter votre police locale ou l'autorité auprès de laquelle vous avez déposé votre signalement.",
    "edit.ante.title": "Modifier le signalement",
    "edit.post.title": "Modifier la fiche de personne non identifiée",
    "edit.sub": "Modifiez n'importe quel champ ci-dessous. Le code de référence reste inchangé.",
    "form.submit.save": "Enregistrer les modifications",
    "common.edit": "Modifier",
    "detail.eyebrow": "Détail du dossier",
    "detail.back": "Retour au tableau de bord",
    "detail.privacy_title": "Ces détails restent confidentiels",
    "detail.privacy_body": "Pour protéger les familles de tout faux espoir et de toute détresse, les détails des dossiers non identifiés ne sont pas affichés ici. En cas d'évolution pertinente de votre dossier, votre référent vous contactera directement.",
    "detail.view": "Voir",
    "detail.imported": "Dossier importé",
    "detail.source": "Source",
    "detail.reported_by": "Votre lien",
    "case.submitted_by": "Signalé par",
    "detail.privacy_cta": "Voir le suivi de votre dossier",
    "detail.copy_link": "Copier le lien",
    "detail.copied": "Lien copié",
    "detail.print": "Imprimer / PDF",
    "detail.export_pdf": "Exporter PDF",
    "detail.pdf_error": "Impossible de générer le PDF.",
    "detail.photos_saved": "Photos mises à jour.",
    "detail.photos_error": "Impossible de mettre à jour les photos.",
    "detail.photo_set_main": "Définir comme principale",
    "detail.photo_remove": "Supprimer la photo",
    "detail.photo_add": "Ajouter une photo",
  },
  de: {
    "nav.home": "Startseite",
    "nav.dashboard": "Übersicht",
    "nav.matches": "Treffer",
    "nav.browse": "Fälle durchsuchen",
    "nav.admin": "Verwaltung",
    "nav.signin": "Anmelden",
    "nav.signout": "Abmelden",
    "nav.create_account": "Konto erstellen",
    "landing.eyebrow": "Vermisste & nicht identifizierte Menschen in Europa",
    "landing.hero_title": "Familien helfen, ihre vermissten Angehörigen zu finden — und Fachleuten helfen, Antworten zu geben.",
    "landing.hero_subtitle": "Familien teilen, was sie über einen vermissten Angehörigen wissen. Forensische Teams teilen, was sie gefunden haben. EuroFind vergleicht beides leise — europaweit — um mögliche Übereinstimmungen früher zu erkennen.",
    "landing.cta_family": "Ich suche nach jemandem",
    "landing.cta_authority": "Ich arbeite bei der Polizei oder in der Forensik",
    "landing.stat.countries": "Abgedeckte Länder",
    "landing.stat.ante": "Vermisstenmeldungen",
    "landing.stat.post": "Fälle nicht identifizierter Personen",
    "landing.stat.users": "Verifizierte Mitglieder",
    "landing.sides_title": "Zwei Seiten, ein gemeinsames Ziel.",
    "landing.sides_subtitle": "Jede Seite trägt getrennt bei, was sie weiß. EuroFind vergleicht die Details — niemals Namen oder Identitäten — und weist auf mögliche Übereinstimmungen zur Prüfung hin.",
    "landing.family_card_title": "Für Familien",
    "landing.authority_card_title": "Für Behörden",
    "landing.how_title": "So funktioniert EuroFind",
    "landing.browse_btn": "Anonymisierte Fälle durchsuchen",
    "landing.ways_title": "Drei Wege, EuroFind voranzubringen.",
    "landing.pos_title": "EuroFind verbindet, was Grenzen, Datenbanken und Jahrzehnte getrennt gehalten haben.",
    "landing.pos_body": "Eine unabhängige europäische Rechercheplattform, entwickelt, um mögliche Verbindungen zwischen vermissten Personen und nicht identifizierten menschlichen Überresten zu entdecken.",
    "landing.pos_complement": "Als Ergänzung zu offiziellen Identifizierungssystemen — niemals als Ersatz: Mögliche Verbindungen werden stets den zuständigen Behörden zur Prüfung vorgelegt.",
    "landing.ways_contribute_title": "Beitragen",
    "landing.ways_contribute_body": "Stellen Sie als autorisierter Partner geeignete Fallinformationen und Fachwissen bereit.",
    "landing.ways_contribute_link": "Als Behörde registrieren",
    "landing.ways_participate_title": "Mitmachen",
    "landing.ways_participate_body": "Helfen Sie, Fallinformationen in Ihrem Land zu prüfen und zu strukturieren.",
    "landing.ways_participate_link": "Helfende Hand werden",
    "landing.ways_fund_title": "Fördern",
    "landing.ways_fund_body": "Unterstützen Sie die Infrastruktur, die Schutzmaßnahmen und das Wachstum des Dienstes.",
    "landing.ways_fund_link": "Kontaktieren Sie uns",
    "landing.footer": "EuroFind ist ein kostenloses humanitäres Werkzeug. Es ersetzt nicht die Polizei oder nationale Behörden. Wenden Sie sich im Notfall oder bei dringenden Informationen zuerst an Ihre örtliche Polizei.",
    "landing.trust_title": "Gemeinsam nach Antworten suchen",
    "landing.trust_body": "Wenn Sie hier sind, weil ein geliebter Mensch vermisst wird, tut uns das sehr leid. Sie müssen nicht allein suchen. EuroFind bringt behutsam zusammen, was Familien wissen und was forensische Teams finden — mit Sorgfalt und ohne falsche Hoffnung. Es ist kostenlos und für Sie da.",
    "landing.family_card_body": "Erstellen Sie eine Vermisstenmeldung — ein Foto, eine Personenbeschreibung, die zuletzt getragene Kleidung, besondere Merkmale. Sie behalten die Kontrolle über Ihre Meldung und sehen mögliche Übereinstimmungen privat ein.",
    "landing.family_b1": "Ein einfaches Formular, Schritt für Schritt",
    "landing.family_b2": "Private Übereinstimmungs-Vorschläge, nur für Sie",
    "landing.family_b3": "Immer kostenlos",
    "landing.family_link": "Familienkonto erstellen",
    "landing.authority_card_body": "Erfassen Sie Fälle nicht identifizierter Personen — geschätzte Altersspanne, Geschlecht, Erkennungsmerkmale sowie Fundort und -datum. Durchsuchen Sie alle Fälle und prüfen Sie vorgeschlagene Übereinstimmungen direkt neben der Vermisstenmeldung.",
    "landing.authority_b1": "Strukturierte forensische Erfassung",
    "landing.authority_b2": "Eine grenzüberschreitend durchsuchbare Datenbank",
    "landing.authority_b3": "Klare, regelbasierte Übereinstimmungs-Vorschläge",
    "landing.authority_link": "Behördenkonto beantragen",
    "landing.step1_title": "Angaben werden erfasst",
    "landing.step1_desc": "Familien und forensische Teams tragen ihr Wissen über einfache, spezielle Formulare ein.",
    "landing.step2_title": "Fälle werden verglichen",
    "landing.step2_desc": "Ein klares, regelbasiertes System bewertet mögliche Übereinstimmungen nach Alter, Geschlecht, Land, körperlichen Merkmalen und besonderen Kennzeichen.",
    "landing.step3_title": "Menschen entscheiden",
    "landing.step3_desc": "Übereinstimmungen sind Vorschläge, niemals Schlussfolgerungen. Forensische Fachleute, Familien und die zuständigen Behörden entscheiden gemeinsam.",
    "auth.signin_title": "Anmelden",
    "auth.signin_sub": "Zugriff auf Ihre Übersicht und den Trefferbereich.",
    "auth.email": "E-Mail",
    "auth.password": "Passwort",
    "auth.no_account": "Neu hier?",
    "auth.create_account": "Konto erstellen",
    "auth.already_account": "Sie haben bereits ein Konto?",
    "auth.signing_in": "Anmeldung läuft…",
    "auth.creating": "Konto wird erstellt…",
    "auth.forgot": "Passwort vergessen?",
    "auth.withdrawn_notice": "Ihr Zugang wurde von einem Administrator entzogen. Bitte wenden Sie sich an Ihre Organisation.",
    "forgot.title": "Passwort zurücksetzen",
    "forgot.sub": "Geben Sie die E-Mail Ihres Kontos ein, und wir senden Ihnen einen Link zum Festlegen eines neuen Passworts.",
    "forgot.submit": "Link senden",
    "forgot.sending": "Senden…",
    "forgot.sent_title": "Prüfen Sie Ihren Posteingang",
    "forgot.sent_sub": "Falls ein Konto mit dieser E-Mail existiert, ist ein Reset-Link unterwegs. Er läuft in 1 Stunde ab.",
    "forgot.back_to_login": "Zurück zur Anmeldung",
    "reset.title": "Neues Passwort wählen",
    "reset.sub": "Geben Sie ein neues Passwort für Ihr Konto ein.",
    "reset.password": "Neues Passwort",
    "reset.confirm": "Passwort bestätigen",
    "reset.submit": "Passwort aktualisieren",
    "reset.updating": "Aktualisierung…",
    "reset.success_title": "Passwort aktualisiert",
    "reset.success_sub": "Sie können sich jetzt mit Ihrem neuen Passwort anmelden.",
    "reset.mismatch": "Die Passwörter stimmen nicht überein.",
    "reset.invalid": "Dieser Reset-Link ist ungültig oder abgelaufen. Bitte fordern Sie einen neuen an.",
    "reset.no_token": "Kein Reset-Token gefunden. Bitte öffnen Sie den Link aus Ihrer E-Mail.",
    "reset.go_login": "Zur Anmeldung",
    "auth.register_title": "Konto erstellen",
    "auth.register_sub": "Wählen Sie, wie Sie EuroFind nutzen möchten.",
    "auth.iam": "Ich bin…",
    "auth.role.family": "Familienangehörige(r)",
    "auth.role.family_sub": "Auf der Suche nach einer vermissten Person",
    "auth.role.authority": "Behörde / Forensik-Expert*in",
    "auth.role.authority_sub": "Arbeit an Fällen mit nicht identifizierten Toten",
    "auth.full_name": "Vollständiger Name",
    "auth.organisation": "Organisation",
    "auth.optional": "(optional)",
    "auth.password_hint": "Mindestens 6 Zeichen.",
    "auth.authority_notice": "Behördenkonten werden von einer Administratorin oder einem Administrator geprüft, bevor Sie Fälle hinzufügen können. Sie können sich sofort anmelden — das Hinzufügen von Einträgen wird nach der Freigabe möglich, in der Regel innerhalb eines Werktags.",
    "dash.welcome": "Willkommen,",
    "dash.workspace.family": "Familien-Arbeitsbereich",
    "dash.workspace.authority": "Behörden-Arbeitsbereich",
    "dash.workspace.admin": "Admin-Arbeitsbereich",
    "dash.family_sub": "Verwalten Sie Ihre eingereichten Meldungen und prüfen Sie mögliche Treffer.",
    "dash.authority_sub": "Erfassen und prüfen Sie Fälle nicht identifizierter Personen und gleichen Sie sie mit Vermisstenmeldungen ab.",
    "dash.admin_sub": "Plattformaktivität prüfen und professionelle Konten freischalten.",
    "dash.new_ante": "Vermisste Person melden",
    "dash.new_post": "Fall nicht identifizierter Person hinzufügen",
    "dash.review_auth": "Behörden prüfen",
    "dash.matches": "Treffer",
    "dash.your_reports": "Ihre Vermisstenmeldungen",
    "dash.your_post": "Ihre Fälle nicht identifizierter Personen",
    "dash.ante_all": "Vermisstenmeldungen in Europa",
    "dash.mine_only": "Nur meine",
    "dash.all_reports": "Alle Meldungen",
    "dash.bulk_select": "Mehrfach löschen",
    "dash.bulk_cancel": "Abbrechen",
    "dash.select_all": "Alle auswählen",
    "dash.selected": "ausgewählt",
    "dash.delete_selected": "Auswahl löschen",
    "dash.bulk_confirm": "Die ausgewählten Fälle endgültig löschen? Dies kann nicht rückgängig gemacht werden.",
    "dash.bulk_deleted": "Fälle gelöscht",
    "dash.filter_contributor": "Beitragende(r)",
    "dash.filter_all_users": "Alle Benutzer",
    "dash.filter_added": "Hinzugefügt",
    "dash.filter_any_time": "Beliebiger Zeitraum",
    "dash.filter_today": "Heute",
    "dash.filter_7d": "Letzte 7 Tage",
    "dash.filter_30d": "Letzte 30 Tage",
    "dash.case_status": "Fallstatus",
    "dash.brand_title": "Sie sind Teil des EuroFind-Netzwerks",
    "dash.brand_body": "Ein humanitäres Netzwerk, das Familien und forensische Teams in ganz Europa verbindet. Zeigen Sie das EuroFind-Zeichen auf Ihrer Website oder Ihren Materialien, um Ihre Teilnahme zu zeigen.",
    "dash.download_logo": "Logo herunterladen",
    "dash.stat_ante_all": "Vermisstenmeldungen (alle)",
    "dash.stat_post": "Fälle nicht identifizierter Personen",
    "dash.stat_matches": "Mögliche Treffer",
    "dash.monitored": "Überwacht",
    "dash.my_imports": "Meine Importe",
    "dash.bulk_import": "Massenimport",
    "dash.reports_count": "Meldungen",
    "dash.ante_empty": "Noch keine Vermisstenmeldungen",
    "dash.ante_empty_sub": "Wenn Familien Fälle hinzufügen, erscheinen sie hier.",
    "dash.no_reports": "Noch keine Meldungen",
    "dash.no_reports_sub": "Beginnen Sie mit einer Vermisstenmeldung. Ihre Daten bleiben privat und werden nur mit Fällen nicht identifizierter Personen verglichen.",
    "dash.no_post": "Noch keine Fälle nicht identifizierter Personen",
    "dash.no_post_sub": "Fügen Sie die Angaben zu einer nicht identifizierten Person hinzu, um grenzüberschreitende Vergleiche zu ermöglichen.",
    "common.delete": "Löschen",
    "common.loading": "Lädt…",
    "common.sign_out": "Abmelden",
    "matches.eyebrow": "Vergleichs-Engine",
    "matches.title": "Mögliche Treffer",
    "matches.sub": "Dies sind Vorschläge aus einem klaren, regelbasierten Vergleich — keine bestätigten Identifikationen. Jede Paarung muss von einer Person geprüft werden. Ein höherer Score bedeutet nur, dass mehr Details übereinstimmen.",
    "matches.min_score": "Mindest-Score",
    "matches.empty_title": "Keine möglichen Treffer",
    "matches.empty_sub": "Sobald mehr Daten zu vermissten und nicht identifizierten Personen vorliegen, erscheinen hier Vorschläge.",
    "matches.score": "Trefferscore",
    "matches.circ_hits": "Übereinstimmende Umstände",
    "review.read_only": "Nur die an diesem Treffer beteiligten Organisationen (oder ein Admin) können den Status ändern oder Notizen hinzufügen.",
    "dash.download_logo_transparent": "Transparentes Logo",
    "imp.eyebrow": "Datenimport",
    "imp.title.ante": "Bulk-Import — vermisste Personen",
    "imp.title.post": "Bulk-Import — nicht identifizierte Personen",
    "imp.tab.ante": "Vermisste Personen",
    "imp.tab.post": "Nicht identifizierte Personen",
    "imp.desc.ante": "Laden Sie eine CSV- oder XLSX-Datei mit öffentlichen, DSGVO-konformen Vermisstenfällen hoch. Die Vorlage erfasst das vollständige körperliche Profil, damit die Matching-Engine mit nicht identifizierten Toten vergleichen kann. Jede Zeile erhält automatisch einen EF-Referenzcode.",
    "imp.desc.post": "Laden Sie eine CSV- oder XLSX-Datei mit Fällen nicht identifizierter Toter hoch (z. B. von einer Behörde, NGO oder von Investigativjournalisten). Pro Zeile ist nur COUNTRY erforderlich; ein Referenzcode wird automatisch erzeugt, wenn das Feld leer bleibt.",
    "imp.required.ante": "Spalten (nur „Full name“ und „Last seen - Country“ sind erforderlich — der Rest ist optional):",
    "imp.required.post": "Spalten (nur COUNTRY ist erforderlich — der Rest ist optional):",
    "imp.template": "Vorlage",
    "imp.drag": "Ziehen Sie eine Datei hierher, oder",
    "imp.choose_file": "Datei auswählen",
    "imp.importing": "Import läuft…",
    "imp.import_records": "Fälle importieren",
    "imp.pdf.title": "Aus einem PDF importieren (KI)",
    "imp.pdf.desc": "Laden Sie ein Falldossier als PDF hoch (Polizeimeldung, Bericht oder gespeicherte Webseite). Die KI liest den Text — auch gescannte Dokumente —, extrahiert Falldaten und Fotos und schlägt Datensätze zur Prüfung vor.",
    "imp.pdf.analyse": "PDF auslesen",
    "imp.pdf.hint": "PDF wird gelesen und Falldaten werden extrahiert — das kann bis zu einer Minute dauern.",
    "imp.url.title": "Von einer Webseite importieren (KI)",
    "imp.url.desc": "Fügen Sie die Adresse einer veröffentlichten Übersichtsseite ein (z. B. eine Polizeiliste). Die KI liest sie, folgt den einzelnen Fallseiten und schlägt Fälle vor, die Sie vor dem Import prüfen können. Verwenden Sie nur Seiten, zu deren Weiterverwendung Ihre Organisation berechtigt ist.",
    "imp.url.label": "Seiten-URL",
    "imp.url.max": "Max. Fälle",
    "imp.url.analyse": "Seite analysieren",
    "imp.url.analysing": "Analysiere…",
    "imp.url.hint": "Die Seite und die Falldetails werden gelesen — das kann bis zu einer Minute dauern.",
    "imp.url.found": "Fall/Fälle gefunden. Haken Sie an, welche importiert werden sollen.",
    "imp.url.select_all": "Alle auswählen",
    "imp.url.deselect_all": "Alle abwählen",
    "imp.url.import_btn": "Auswahl importieren",
    "imp.url.empty": "Die KI konnte aus dieser Seite keine Fälle extrahieren. Möglicherweise lädt sie ihre Daten per JavaScript oder verwendet ein ungewöhnliches Layout. Versuchen Sie eine andere Übersichtsseite oder nutzen Sie den CSV-Import oben.",
    "imp.url.imported": "Importiert:",
    "imp.skipped_lc": "übersprungen",
    "imp.errors_lc": "Fehler",
    "imp.summary": "Import-Zusammenfassung",
    "imp.created": "Erstellt",
    "imp.skipped": "Übersprungen (leer)",
    "imp.errors": "Fehler",
    "imp.all_ok": "Alle gültigen Zeilen wurden erfolgreich importiert.",
    "imp.some_failed": "Einige Zeilen konnten nicht importiert werden:",
    "imp.row": "Zeile",
    "thanks.title": "Danke — wir haben Ihre Meldung erhalten",
    "thanks.body1": "Vielen Dank, dass Sie uns Ihre Meldung anvertraut haben. Wir wissen, wie schwer dieser Schritt sein kann.",
    "thanks.body2": "Ihre Angaben werden vertraulich mit Daten forensischer Dienste und Behörden in ganz Europa verglichen.",
    "thanks.body3": "Sollte es eine relevante Entwicklung geben, melden wir uns — oder eine unserer lokalen Kontaktpersonen — persönlich bei Ihnen. Sie müssen nichts weiter tun.",
    "thanks.body4": "Wir können nichts über den Ausgang versprechen, aber jede Meldung stärkt die Suche.",
    "thanks.back": "Zurück zu meinem Dashboard",
    "matches.ante": "Vermisste Person",
    "matches.post": "Nicht identifizierte Person",
    "matches.timeline_warning_title": "Zeitlicher Widerspruch — manuelle Prüfung erforderlich",
    "matches.timeline_warning_body": "Das Fundfdatum liegt vor dem Datum, an dem die Person zuletzt gesehen wurde — auf dem Papier ist diese Paarung also unmöglich. Dennoch stimmt sie in allen anderen Details sehr stark überein — eine späte oder ungenaue Vermisstenmeldung kann das verursachen. Bitte prüfen Sie die Daten von Hand.",
    "matches.date_last_seen": "Zuletzt gesehen",
    "matches.date_found": "Gefunden",
    "review.status_label": "Status",
    "review.status.new": "Neu",
    "review.status.investigating": "In Untersuchung",
    "review.status.excluded": "Ausgeschlossen",
    "review.status.confirmed": "Bestätigt",
    "review.log_title": "Ermittlungsprotokoll",
    "review.no_notes": "Noch keine Notizen.",
    "matches.count_label": "Treffer",
    "matches.load_more": "Weitere Ergebnisse laden",
    "matches.top_shown": "Top {shown} von {total} angezeigt",
    "form.contact_name": "Kontaktperson (Name)",
    "form.contact_phone": "Kontakttelefon",
    "form.contact_email": "Kontakt-E-Mail",
    "review.status_admin_only": "Nur der EuroFind-Administrator kann diesen Status ändern — hinterlassen Sie eine Notiz mit Ihren Erkenntnissen.",
    "imp.type_warn.ante": "Sieht wie ein nicht identifizierter Leichnam aus — vor dem Import als vermisste Person prüfen",
    "imp.type_warn.post": "Sieht wie eine vermisste Person aus — vor dem Import als nicht identifizierter Leichnam prüfen",
    "imp.merged": "Zusammengeführt",
    "imp.merged_note": "Duplikate — der bestehende Fall wurde ergänzt statt neu angelegt",
    "detail.merged_from": "Ergänzt aus",
    "help.title": "Internationale helfende Hände",
    "help.subtitle": "Helfen Sie EuroFind zu wachsen: Sammeln Sie öffentliche Fälle aus Ihrem Land, füllen Sie unsere Meldebögen aus und laden Sie sie zur Prüfung hoch.",
    "help.apply_tab": "Code beantragen",
    "help.upload_tab": "Ich habe bereits einen Code",
    "help.photos": "Fotos der Person (optional, max. 6)",
    "help.photos_count": "Foto(s)",
    "help.thanks_stats": "Sie haben nun insgesamt {total} Bögen beigetragen — {approved} genehmigt und auf der Plattform online.",
    "help.kind_corrected": "{n} Bögen wurden automatisch in die richtige Kategorie verschoben: Der Bogentyp im PDF entsprach nicht Ihrer Auswahl.",
    "help.thanks_milestone": "Ein herzliches Dankeschön! Mit {approved} genehmigten Bögen gehören Sie zu unseren wertvollsten Helfenden.",
    "help.photos_hint": "JPG/PNG — nach Genehmigung werden diese Fotos mit dem Fall auf der Plattform angezeigt.",
    "help.photos_single": "Fotos können nur beim Hochladen eines einzelnen Bogens hinzugefügt werden, damit klar ist, zu welchem Fall sie gehören.",
    "help.applied_next": "Nach der Genehmigung erhalten Sie Ihren persönlichen Code — kommen Sie dann hierher zurück und wählen Sie „Ich habe bereits einen Code“, um Ihre Bögen hochzuladen.",
    "help.name": "Vollständiger Name",
    "help.email": "E-Mail-Adresse",
    "help.org": "Organisation (optional)",
    "help.scope": "Was möchten Sie abdecken?",
    "help.scope_hint": "z.B. „alle nicht identifizierten Leichname in Bulgarien“",
    "help.apply_btn": "Bewerben",
    "help.applied": "Danke! Ihre Bewerbung ist eingegangen — das EuroFind-Team meldet sich mit Ihrem persönlichen EuroFind-Code.",
    "help.status_pending": "Ihre Bewerbung ist noch in Bearbeitung — das EuroFind-Team meldet sich mit Ihrem persönlichen Code.",
    "help.status_approved": "Ihre Bewerbung wurde bereits genehmigt — nutzen Sie Ihren EuroFind-Code im Upload-Tab.",
    "help.status_rejected": "Ihre Bewerbung wurde nicht genehmigt. Kontaktieren Sie das EuroFind-Team für mehr Informationen.",
    "help.code": "Ihr EuroFind-Code",
    "help.kind": "Was laden Sie hoch?",
    "help.kind_am": "Vermisste Personen (grüner Bogen)",
    "help.kind_pm": "Nicht identifizierte Leichname (blauer Bogen)",
    "help.templates": "Meldebögen herunterladen",
    "help.tpl_am": "Bogen vermisste Person",
    "help.tpl_pm": "Bogen nicht identifizierte Person",
    "help.files": "Ausgefüllte PDF-Bögen (max. 10)",
    "help.upload_btn": "Zu EuroFind hochladen",
    "help.uploaded": "Erhalten! Ihre Bögen warten auf die Prüfung durch das EuroFind-Team und erscheinen nach Genehmigung auf der Plattform.",
    "help.upload_more": "Weitere Bögen hochladen",
    "help.how_title": "So funktioniert es",
    "help.how1": "Bewerben Sie sich mit Name und E-Mail-Adresse.",
    "help.how2": "Nach Genehmigung erhalten Sie Ihren persönlichen EuroFind-Code und die Meldebögen.",
    "help.how3": "Füllen Sie pro Fall einen Bogen aus — Ihr EuroFind-Code oben — und laden Sie sie hier mit E-Mail + Code hoch.",
    "help.how_photos": "Fügen Sie Fotos nie in das PDF ein — laden Sie sie stattdessen im Upload-Formular hoch: Jeder Bogen hat ein eigenes Foto-Feld (max. 6), so bleibt jedes Foto dem richtigen Fall zugeordnet.",
    "help.how4": "Das EuroFind-Team prüft jeden Bogen; genehmigte Fälle erscheinen auf der Plattform.",
    "help.landing_eyebrow": "Für Freiwillige, Studierende & Forschende",
    "help.landing_title": "Helfen Sie uns von überall in Europa",
    "help.landing_text": "Nehmen Sie ein Land unter Ihre Fittiche und reichen Sie Fälle mit unseren fertigen Meldebögen ein. Jeder Bogen wird geprüft, bevor er auf der Plattform erscheint.",
    "help.landing_cta": "Helfende Hand werden",
    "kind.ante": "Vermisste Person",
    "kind.post": "Nicht identifiziert",
    "notif.helper_apply_msg": "{author} hat sich als helfende Hand beworben — Bewerbung prüfen",
    "notif.helper_upload_msg": "{author} hat {n} Meldebögen in die Wartebox hochgeladen",
    "detail.back_list": "Zurück zu den Ergebnissen",
    "browse.save_search": "Suche speichern",
    "browse.saved": "Gespeicherte Suchen",
    "browse.name_prompt": "Name für diese Suche:",
    "browse.saved_ok": "Suche gespeichert",
    "browse.load_more": "Mehr laden",
    "form.source_ref": "Quellen-Referenz",
    "match.new_hint": "Ein Match bleibt „Neu“, bis eine beteiligte Organisation einen Status setzt oder eine Notiz hinzufügt.",
    "match.compare": "Nebeneinander",
    "match.compare_title": "Match-Vergleich",
    "match.highmatch_title": "Hoher Match — was nun?",
    "match.highmatch_body": "Prüfen Sie beide Akten sorgfältig, kontaktieren Sie die Organisation der anderen Akte (siehe Kontaktdaten der Akte) und halten Sie Ihre Erkenntnisse unten über Status und Notizen fest. Die EuroFind-Verwaltung (BVM) wird bei Matches ab 85 automatisch alarmiert und kann länderübergreifend koordinieren.",
    "notif.title": "Benachrichtigungen",
    "notif.empty": "Noch keine Benachrichtigungen.",
    "notif.note_msg": "{author} hat eine Notiz zu Match {a} × {b} hinzugefügt",
    "notif.status_msg": "{author} hat Match {a} × {b} auf {status} gesetzt",
    "review.note_placeholder": "Notiz zu diesem Treffer hinzufügen…",
    "review.add_note": "Notiz hinzufügen",
    "review.show_details": "Details anzeigen",
    "review.hide_details": "Ausblenden",
    "review.hide_excluded": "Ausgeschlossene Treffer ausblenden",
    "review.updated_by": "Aktualisiert von",
    "browse.title": "Fälle durchsuchen",
    "browse.sub": "Suchen Sie nach Name, Land, Stadt, körperlichen Merkmalen oder Besonderheiten.",
    "browse.search": "Suchen…",
    "browse.tab_post": "Nicht identifizierte Personen",
    "browse.tab_ante": "Vermisste Personen",
    "browse.locked_title": "Melden Sie sich an, um Fälle zu durchsuchen",
    "browse.locked_sub": "Aus Respekt und Datenschutzgründen ist das Durchsuchen verifizierten Konten vorbehalten.",
    "form.section.id": "Identifikation",
    "form.section.demo": "Demografie",
    "form.section.physical": "Körperliche Beschreibung",
    "form.section.last_seen": "Zuletzt gesehen",
    "form.section.found": "Fundort & -datum",
    "form.section.photo": "Foto",
    "form.full_name": "Vollständiger Name der vermissten Person *",
    "form.ref_code": "Interner Aktenzeichen (optional)",
    "form.relation": "Ihr Verhältnis zur Person (optional)",
    "form.age_min": "Geschätztes Alter (min.)",
    "form.age_max": "Geschätztes Alter (max.)",
    "form.age": "Alter",
    "form.dob": "Geburtsdatum",
    "form.age_at_disappearance": "Alter beim Verschwinden",
    "form.age_unknown_dob": "Oder Alter manuell eingeben (falls Geburtsdatum unbekannt)",
    "form.height_min": "Größe (min, cm)",
    "form.height_max": "Größe (max, cm)",
    "form.gender": "Geschlecht",
    "form.gender.male": "Männlich",
    "form.gender.female": "Weiblich",
    "form.gender.unknown": "Unbekannt",
    "form.height": "Größe (cm)",
    "form.weight": "Gewicht (kg)",
    "form.hair": "Haarfarbe",
    "form.eye": "Augenfarbe",
    "attr.black": "Schwarz",
    "attr.brown": "Braun",
    "attr.blond": "Blond",
    "attr.red": "Rot",
    "attr.grey": "Grau",
    "attr.white": "Weiß",
    "attr.bald": "Glatze",
    "attr.blue": "Blau",
    "attr.green": "Grün",
    "attr.hazel": "Haselnussbraun",
    "attr.amber": "Bernstein",
    "tag.hair": "Haare",
    "tag.eyes": "Augen",
    "form.dob_day": "TT",
    "form.dob_month": "MM",
    "form.dob_year": "JJJJ",
    "form.dob_hint": "Nur das Jahr genügt, wenn das genaue Datum unbekannt ist.",
    "detail.translate": "Details übersetzen",
    "detail.show_original": "Original anzeigen",
    "detail.translated_badge": "Automatische Übersetzung",
    "detail.translate_error": "Übersetzung fehlgeschlagen. Bitte erneut versuchen.",
    "detail.photos_unsaved": "Nicht gespeicherte Foto-Änderungen",
    "detail.photos_save": "Änderungen speichern",
    "detail.photos_cancel": "Abbrechen",
    "match.field.age": "Altersspanne",
    "match.field.circumstances": "Umstände",
    "match.field.gender_partial": "Geschlecht unbekannt (teilweise)",
    "form.ethnicity": "Ethnische Herkunft",
    "form.marks": "Besondere Merkmale (Narben, Tattoos, Muttermale)",
    "form.clothing": "Kleidung oder Accessoires",
    "form.country": "Land *",
    "form.location": "Stadt oder Region",
    "form.date_seen": "Datum der letzten Sichtung",
    "form.date_found": "Funddatum",
    "form.pmi": "Geschätzte Zeit seit dem Tod (Tage)",
    "form.description.ante": "Umstände und weitere Hinweise",
    "form.description.post": "Fallbeschreibung",
    "form.case_notes": "Interne Notizen",
    "form.photo_hint": "Verwenden Sie ein klares, identifizierbares Foto. Max. 8 MB. JPG, PNG oder WEBP.",
    "form.upload": "Foto hochladen",
    "form.uploading": "Hochladen…",
    "form.submit.ante": "Vermisstenmeldung einreichen",
    "form.submit.post": "Fall nicht identifizierter Person speichern",
    "form.saving": "Speichern…",
    "form.required_error": "Bitte füllen Sie die hervorgehobenen Pflichtfelder aus.",
    "new.ante.title": "Vermisste Person melden",
    "new.post.title": "Fall nicht identifizierter Person hinzufügen",
    "new.ante.sub": "Bitte machen Sie so genaue Angaben wie möglich. Jedes Feld hilft der Engine, sinnvolle Vorschläge zu finden.",
    "new.post.sub": "Geben Sie die strukturierten Daten des Falls ein. Die Informationen sind nur für verifizierte Konten zugänglich.",
    "dash.new_case": "Neuen Fall hinzufügen",
    "choose.title": "Was möchten Sie erfassen?",
    "choose.sub": "Wählen Sie zuerst die Art des Falls — das Formular passt sich Ihrer Wahl an.",
    "choose.post.title": "Nicht identifizierte Person",
    "choose.post.desc": "Eine verstorbene Person, deren Identität unbekannt ist. Kein Name nötig — Sie füllen ein Post-mortem-Formular mit Referenzcode aus.",
    "choose.ante.title": "Vermisste Person",
    "choose.ante.desc": "Eine als vermisst gemeldete Person. Sie füllen ein Ante-mortem-Formular mit Name und Details aus.",
    "choose.single": "Einen Fall erfassen",
    "choose.bulk": "Bulk-Import (CSV / URL)",
    "admin.eyebrow": "Verwaltung · Prüfung",
    "admin.title": "Anträge auf Behördenzugang",
    "admin.sub": "Prüfen Sie professionelle Konten, die Zugang beantragen. Nur freigegebene Behörden können Fälle nicht identifizierter Personen hinzufügen oder bearbeiten.",
    "admin.pending": "Ausstehend",
    "admin.approved": "Freigegeben",
    "admin.rejected": "Abgelehnt",
    "admin.approve": "Freigeben",
    "admin.reject": "Ablehnen",
    "admin.empty": "Nichts in dieser Liste.",
    "invite.panel.title": "Einladungs-Links",
    "invite.panel.sub": "Erstellen Sie einmalige, vorab freigegebene Links für vertrauenswürdige Kolleg*innen. Wer sich über den Link registriert, wird sofort als aktive Behörde freigeschaltet — keine weitere Admin-Prüfung nötig.",
    "invite.new": "Neuer Einladungs-Link",
    "invite.create_title": "Einladungs-Link erstellen",
    "invite.restrict_email": "Auf E-Mail beschränken (optional)",
    "invite.restrict_hint": "Wenn gesetzt, kann nur diese Adresse den Link nutzen.",
    "invite.expires": "Läuft ab in (Tagen)",
    "invite.note": "Interne Notiz (optional)",
    "invite.create_btn": "Link erstellen",
    "invite.creating": "Erstellen…",
    "invite.created_title": "Einladungs-Link erstellt",
    "invite.created_sub": "Teilen Sie diesen Link mit Ihrer Kollegin oder Ihrem Kollegen. Er ist einmalig und läuft ab am",
    "invite.copy": "Link kopieren",
    "invite.copied": "Kopiert",
    "invite.revoke": "Widerrufen",
    "invite.done": "Fertig",
    "invite.page_badge": "Vorab freigegebene Behörden-Einladung",
    "invite.page_title": "Behördenkonto einrichten",
    "invite.page_sub": "Ihr Konto wird bei der Registrierung automatisch aktiviert.",
    "invite.locked_hint": "Diese Einladung ist an diese E-Mail gebunden.",
    "invite.submit": "Behördenkonto aktivieren",
    "invite.invalid_title": "Einladung nicht verfügbar",
    "invite.back_home": "Zurück zur Startseite",
    "invite.verifying": "Einladung wird geprüft…",
    "pending.title": "Ihr Behördenkonto wird geprüft",
    "pending.rejected_title": "Konto nicht freigegeben",
    "pending.sub": "Vielen Dank für Ihre Registrierung. Ein*e Administrator*in prüft Ihre beruflichen Angaben, bevor Sie Fälle hinzufügen und Treffer sehen können. Das dauert in der Regel etwa einen Werktag.",
    "pending.rejected_sub": "Ein*e Administrator*in hat Ihre Anfrage geprüft und konnte Ihr Behördenkonto nicht freigeben. Falls Sie dies für einen Irrtum halten, kontaktieren Sie bitte die EuroFind-Administrator*innen mit zusätzlichen Nachweisen.",
    "family.status.title": "Ihr Fall wird überwacht",
    "family.status.body": "Ihre Meldung wird fortlaufend und vertraulich mit Datensätzen in ganz Europa abgeglichen. Um Sie vor Belastung zu schützen, zeigen wir hier keine Vergleichsdetails. Ihr Ansprechpartner kontaktiert Sie direkt, falls etwas Relevantes gefunden wird.",
    "family.status.contact_title": "Ihr Ansprechpartner",
    "family.status.contact_sub": "Bei Fragen zu Ihrer Meldung wenden Sie sich bitte an den Ansprechpartner für Ihre Region.",
    "family.status.no_liaison": "Ein dedizierter Ansprechpartner für Ihre Region wird zugewiesen. Wenden Sie sich in der Zwischenzeit an Ihre örtliche Polizei oder die Behörde, bei der Sie Ihre Vermisstenmeldung aufgegeben haben.",
    "edit.ante.title": "Vermisstenmeldung bearbeiten",
    "edit.post.title": "Fall nicht identifizierter Person bearbeiten",
    "edit.sub": "Bearbeiten Sie unten ein beliebiges Feld. Das Aktenzeichen bleibt unverändert.",
    "form.submit.save": "Änderungen speichern",
    "common.edit": "Bearbeiten",
    "detail.eyebrow": "Falldetails",
    "detail.back": "Zurück zur Übersicht",
    "detail.privacy_title": "Diese Details bleiben vertraulich",
    "detail.privacy_body": "Um Familien vor falschen Hoffnungen und Belastung zu schützen, werden die Details nicht identifizierter Datensätze hier nicht angezeigt. Bei einer relevanten Entwicklung in Ihrem Fall kontaktiert Sie Ihr Ansprechpartner direkt.",
    "detail.view": "Ansehen",
    "detail.imported": "Importierter Datensatz",
    "detail.source": "Quelle",
    "detail.reported_by": "Ihr Verhältnis",
    "case.submitted_by": "Gemeldet von",
    "detail.privacy_cta": "Ihren Fallstatus ansehen",
    "detail.copy_link": "Link kopieren",
    "detail.copied": "Link kopiert",
    "detail.print": "Drucken / PDF",
    "detail.export_pdf": "PDF exportieren",
    "detail.pdf_error": "PDF konnte nicht erstellt werden.",
    "detail.photos_saved": "Fotos aktualisiert.",
    "detail.photos_error": "Fotos konnten nicht aktualisiert werden.",
    "detail.photo_set_main": "Als Hauptfoto festlegen",
    "detail.photo_remove": "Foto entfernen",
    "detail.photo_add": "Foto hinzufügen",
  },
  es: {
    "nav.home": "Inicio",
    "nav.dashboard": "Panel",
    "nav.matches": "Coincidencias",
    "nav.browse": "Explorar casos",
    "nav.admin": "Administración",
    "nav.signin": "Iniciar sesión",
    "nav.signout": "Cerrar sesión",
    "nav.create_account": "Crear cuenta",
    "landing.eyebrow": "Personas desaparecidas y sin identificar en Europa",
    "landing.hero_title": "Ayudamos a las familias a encontrar a quienes han perdido, y a los expertos a darles respuestas.",
    "landing.hero_subtitle": "Las familias comparten lo que saben de un ser querido desaparecido. Los equipos forenses comparten lo que han encontrado. EuroFind compara ambos discretamente — en toda Europa — para detectar antes posibles coincidencias.",
    "landing.cta_family": "Estoy buscando a alguien",
    "landing.cta_authority": "Trabajo en policía o ciencias forenses",
    "landing.stat.countries": "Países cubiertos",
    "landing.stat.ante": "Informes de personas desaparecidas",
    "landing.stat.post": "Fichas de personas sin identificar",
    "landing.stat.users": "Miembros verificados",
    "landing.sides_title": "Dos lados, un objetivo común.",
    "landing.sides_subtitle": "Cada parte aporta lo que sabe por separado. EuroFind compara los detalles — nunca nombres ni identidades — y señala posibles coincidencias para que las revisen personas.",
    "landing.family_card_title": "Para las familias",
    "landing.authority_card_title": "Para las autoridades",
    "landing.how_title": "Cómo funciona EuroFind",
    "landing.browse_btn": "Explorar fichas anonimizadas",
    "landing.ways_title": "Tres formas de impulsar EuroFind.",
    "landing.pos_title": "EuroFind conecta lo que las fronteras, las bases de datos y las décadas han mantenido separado.",
    "landing.pos_body": "Una plataforma de investigación europea independiente, diseñada para descubrir posibles vínculos entre personas desaparecidas y restos humanos sin identificar.",
    "landing.pos_complement": "Complementando los sistemas oficiales de identificación — nunca sustituyéndolos: los posibles vínculos se presentan siempre a las autoridades competentes para su verificación.",
    "landing.ways_contribute_title": "Contribuir",
    "landing.ways_contribute_body": "Aporte información de casos elegibles y su experiencia como socio autorizado.",
    "landing.ways_contribute_link": "Registrarse como autoridad",
    "landing.ways_participate_title": "Participar",
    "landing.ways_participate_body": "Ayude a revisar y estructurar la información de casos en su país.",
    "landing.ways_participate_link": "Convertirse en mano amiga",
    "landing.ways_fund_title": "Financiar",
    "landing.ways_fund_body": "Patrocine la infraestructura, las salvaguardias y el crecimiento del servicio.",
    "landing.ways_fund_link": "Póngase en contacto con nosotros",
    "landing.footer": "EuroFind es una herramienta humanitaria gratuita. No sustituye a la policía ni a las autoridades nacionales. En caso de emergencia, o si tiene información urgente, contacte primero con su policía local.",
    "landing.trust_title": "Buscar respuestas, juntos",
    "landing.trust_body": "Si estás aquí porque un ser querido ha desaparecido, lo sentimos mucho. No tienes que buscar en soledad. EuroFind une con delicadeza lo que saben las familias y lo que encuentran los equipos forenses — con cuidado y sin falsas esperanzas. Es gratis y está aquí para ti.",
    "landing.family_card_body": "Añade un informe de persona desaparecida: una foto, una descripción física, la ropa que llevaba la última vez, señas particulares. Mantienes el control de tu informe y consultas las posibles coincidencias en privado.",
    "landing.family_b1": "Un formulario sencillo, paso a paso",
    "landing.family_b2": "Sugerencias de coincidencias privadas, solo para ti",
    "landing.family_b3": "Siempre gratis",
    "landing.family_link": "Crear una cuenta de familia",
    "landing.authority_card_body": "Añade fichas de personas sin identificar: rango de edad estimado, sexo, rasgos identificativos, y dónde y cuándo fueron halladas. Explora todas las fichas y revisa las coincidencias sugeridas junto al informe de desaparición.",
    "landing.authority_b1": "Registro forense estructurado",
    "landing.authority_b2": "Una base de datos consultable entre países",
    "landing.authority_b3": "Sugerencias de coincidencia claras y basadas en reglas",
    "landing.authority_link": "Solicitar una cuenta de autoridad",
    "landing.step1_title": "Se introducen los datos",
    "landing.step1_desc": "Las familias y los equipos forenses añaden lo que saben mediante formularios sencillos y específicos.",
    "landing.step2_title": "Se comparan los casos",
    "landing.step2_desc": "Un motor claro y basado en reglas puntúa las posibles coincidencias según edad, sexo, país, rasgos físicos y señas particulares.",
    "landing.step3_title": "Las personas deciden",
    "landing.step3_desc": "Las coincidencias son sugerencias, nunca conclusiones. Los expertos forenses, las familias y las autoridades competentes deciden juntos.",
    "auth.signin_title": "Iniciar sesión",
    "auth.signin_sub": "Accede a tu panel y al espacio de coincidencias.",
    "auth.email": "Correo electrónico",
    "auth.password": "Contraseña",
    "auth.no_account": "¿Nuevo aquí?",
    "auth.create_account": "Crear una cuenta",
    "auth.already_account": "¿Ya tienes cuenta?",
    "auth.signing_in": "Iniciando…",
    "auth.creating": "Creando cuenta…",
    "auth.forgot": "¿Olvidó su contraseña?",
    "auth.withdrawn_notice": "Un administrador ha retirado su acceso. Póngase en contacto con su organización para más detalles.",
    "forgot.title": "Restablecer su contraseña",
    "forgot.sub": "Introduzca el correo de su cuenta y le enviaremos un enlace para establecer una nueva contraseña.",
    "forgot.submit": "Enviar enlace",
    "forgot.sending": "Enviando…",
    "forgot.sent_title": "Revise su bandeja de entrada",
    "forgot.sent_sub": "Si existe una cuenta con ese correo, el enlace de restablecimiento está en camino. Caduca en 1 hora.",
    "forgot.back_to_login": "Volver a iniciar sesión",
    "reset.title": "Elija una nueva contraseña",
    "reset.sub": "Introduzca una nueva contraseña para su cuenta.",
    "reset.password": "Nueva contraseña",
    "reset.confirm": "Confirmar contraseña",
    "reset.submit": "Actualizar contraseña",
    "reset.updating": "Actualizando…",
    "reset.success_title": "Contraseña actualizada",
    "reset.success_sub": "Ahora puede iniciar sesión con su nueva contraseña.",
    "reset.mismatch": "Las contraseñas no coinciden.",
    "reset.invalid": "Este enlace de restablecimiento no es válido o ha caducado. Solicite uno nuevo.",
    "reset.no_token": "No se encontró el token de restablecimiento. Abra el enlace de su correo.",
    "reset.go_login": "Ir a iniciar sesión",
    "auth.register_title": "Crea tu cuenta",
    "auth.register_sub": "Elige cómo usarás EuroFind.",
    "auth.iam": "Soy…",
    "auth.role.family": "Familiar",
    "auth.role.family_sub": "Buscando a un ser querido desaparecido",
    "auth.role.authority": "Autoridad / Experto forense",
    "auth.role.authority_sub": "Trabajando en casos de cuerpos no identificados",
    "auth.full_name": "Nombre completo",
    "auth.organisation": "Organización",
    "auth.optional": "(opcional)",
    "auth.password_hint": "Al menos 6 caracteres.",
    "auth.authority_notice": "Las cuentas de autoridad son revisadas por un administrador antes de que puedas añadir casos. Puedes iniciar sesión de inmediato — añadir fichas se habilita una vez aprobada tu cuenta, normalmente en un día hábil.",
    "dash.welcome": "Bienvenido,",
    "dash.workspace.family": "Espacio familiar",
    "dash.workspace.authority": "Espacio de autoridad",
    "dash.workspace.admin": "Espacio de administración",
    "dash.family_sub": "Gestiona los informes que has enviado y revisa posibles coincidencias.",
    "dash.authority_sub": "Añade y revisa fichas de personas sin identificar, y compáralas con los informes de personas desaparecidas.",
    "dash.admin_sub": "Revisa la actividad de la plataforma y aprueba cuentas profesionales.",
    "dash.new_ante": "Reportar una persona desaparecida",
    "dash.new_post": "Añadir ficha de persona sin identificar",
    "dash.review_auth": "Revisar autoridades",
    "dash.matches": "Coincidencias",
    "dash.your_reports": "Tus informes de personas desaparecidas",
    "dash.your_post": "Tus fichas de personas sin identificar",
    "dash.ante_all": "Informes de personas desaparecidas en Europa",
    "dash.mine_only": "Solo los míos",
    "dash.all_reports": "Todos los informes",
    "dash.bulk_select": "Borrado múltiple",
    "dash.bulk_cancel": "Cancelar",
    "dash.select_all": "Seleccionar todo",
    "dash.selected": "seleccionado(s)",
    "dash.delete_selected": "Eliminar selección",
    "dash.bulk_confirm": "¿Eliminar definitivamente los casos seleccionados? Esta acción no se puede deshacer.",
    "dash.bulk_deleted": "casos eliminados",
    "dash.filter_contributor": "Colaborador",
    "dash.filter_all_users": "Todos los usuarios",
    "dash.filter_added": "Añadido",
    "dash.filter_any_time": "Cualquier fecha",
    "dash.filter_today": "Hoy",
    "dash.filter_7d": "Últimos 7 días",
    "dash.filter_30d": "Últimos 30 días",
    "dash.case_status": "Estado del caso",
    "dash.brand_title": "Formas parte de la red EuroFind",
    "dash.brand_body": "Una red humanitaria que conecta a familias y equipos forenses de toda Europa. Muestra la marca EuroFind en tu web o materiales para dar a conocer tu participación.",
    "dash.download_logo": "Descargar logo",
    "dash.stat_ante_all": "Informes de personas desaparecidas (todos)",
    "dash.stat_post": "Fichas de personas sin identificar",
    "dash.stat_matches": "Coincidencias potenciales",
    "dash.monitored": "En seguimiento",
    "dash.my_imports": "Mis importaciones",
    "dash.bulk_import": "Importación masiva",
    "dash.reports_count": "informes",
    "dash.ante_empty": "Aún no hay informes de personas desaparecidas",
    "dash.ante_empty_sub": "Cuando las familias añadan casos, aparecerán aquí.",
    "dash.no_reports": "Aún no hay informes",
    "dash.no_reports_sub": "Comience añadiendo un informe de persona desaparecida. Su información permanece privada y solo se compara con fichas de personas sin identificar.",
    "dash.no_post": "Aún no hay fichas de personas sin identificar",
    "dash.no_post_sub": "Añada los datos de una persona sin identificar para permitir la comparación transfronteriza.",
    "common.delete": "Eliminar",
    "common.loading": "Cargando…",
    "common.sign_out": "Cerrar sesión",
    "matches.eyebrow": "Motor de comparación",
    "matches.title": "Coincidencias potenciales",
    "matches.sub": "Son sugerencias de una comparación clara y basada en reglas, no identificaciones confirmadas. Cada emparejamiento debe revisarlo una persona. Una puntuación más alta solo significa que coinciden más detalles.",
    "matches.min_score": "Puntuación mínima",
    "matches.empty_title": "Sin coincidencias potenciales",
    "matches.empty_sub": "A medida que lleguen más datos de personas desaparecidas y sin identificar, aquí aparecerán sugerencias.",
    "matches.score": "Puntuación",
    "matches.circ_hits": "Circunstancias coincidentes",
    "review.read_only": "Solo las organizaciones implicadas en esta coincidencia (o un administrador) pueden cambiar su estado o añadir notas.",
    "dash.download_logo_transparent": "Logo transparente",
    "imp.eyebrow": "Importación de datos",
    "imp.title.ante": "Importación masiva — personas desaparecidas",
    "imp.title.post": "Importación masiva — personas sin identificar",
    "imp.tab.ante": "Personas desaparecidas",
    "imp.tab.post": "Personas sin identificar",
    "imp.desc.ante": "Suba un CSV o XLSX con registros públicos de personas desaparecidas (conformes con el RGPD). La plantilla recoge el perfil físico completo para que el motor de coincidencias pueda comparar con cuerpos sin identificar. Cada fila recibe automáticamente un código de referencia EF.",
    "imp.desc.post": "Suba un CSV o XLSX con registros de cuerpos sin identificar (p. ej., de una autoridad, ONG o periodista de investigación). Solo COUNTRY es obligatorio por fila; el código de referencia se genera automáticamente si se deja vacío.",
    "imp.required.ante": "Columnas (solo «Full name» y «Last seen - Country» son obligatorias — el resto es opcional):",
    "imp.required.post": "Columnas (solo COUNTRY es obligatoria — el resto es opcional):",
    "imp.template": "Plantilla",
    "imp.drag": "Arrastre un archivo aquí, o",
    "imp.choose_file": "Elegir archivo",
    "imp.importing": "Importando…",
    "imp.import_records": "Importar fichas",
    "imp.pdf.title": "Importar desde un PDF (IA)",
    "imp.pdf.desc": "Sube un expediente en PDF (aviso policial, informe o página web guardada). La IA lee el texto —incluidos documentos escaneados—, extrae los datos y las fotos y propone registros para revisar antes de importar.",
    "imp.pdf.analyse": "Leer PDF",
    "imp.pdf.hint": "Leyendo el PDF y extrayendo los datos — puede tardar hasta un minuto.",
    "imp.url.title": "Importar desde una página web (IA)",
    "imp.url.desc": "Pegue la dirección de una página de resumen publicada (p. ej., una lista policial). La IA la lee, sigue las páginas de cada caso y propone fichas que puede revisar antes de importar. Use solo páginas que su organización esté autorizada a reutilizar.",
    "imp.url.label": "URL de la página",
    "imp.url.max": "Casos máx.",
    "imp.url.analyse": "Analizar página",
    "imp.url.analysing": "Analizando…",
    "imp.url.hint": "Leyendo la página y los detalles de los casos — puede tardar hasta un minuto.",
    "imp.url.found": "ficha(s) encontrada(s). Marque las que desee importar.",
    "imp.url.select_all": "Seleccionar todo",
    "imp.url.deselect_all": "Deseleccionar todo",
    "imp.url.import_btn": "Importar selección",
    "imp.url.empty": "La IA no pudo extraer ninguna ficha de esa página. Puede que dependa de JavaScript para cargar sus datos o que use un diseño inusual. Pruebe con otra página de resumen o use la importación CSV de arriba.",
    "imp.url.imported": "Importadas:",
    "imp.skipped_lc": "omitida(s)",
    "imp.errors_lc": "error(es)",
    "imp.summary": "Resumen de la importación",
    "imp.created": "Creadas",
    "imp.skipped": "Omitidas (vacías)",
    "imp.errors": "Errores",
    "imp.all_ok": "Todas las filas válidas se importaron correctamente.",
    "imp.some_failed": "Algunas filas no se pudieron importar:",
    "imp.row": "Fila",
    "thanks.title": "Gracias — hemos recibido su informe",
    "thanks.body1": "Gracias por confiarnos su informe. Sabemos lo difícil que puede ser este paso.",
    "thanks.body2": "Su información se comparará, de forma confidencial, con los datos compartidos por servicios forenses y autoridades de toda Europa.",
    "thanks.body3": "Si hay una novedad relevante, nosotros — o uno de nuestros enlaces locales — nos pondremos en contacto con usted personalmente. No necesita hacer nada más.",
    "thanks.body4": "No podemos prometer resultados, pero sepa que cada informe fortalece la búsqueda.",
    "thanks.back": "Volver a mi panel",
    "matches.ante": "Persona desaparecida",
    "matches.post": "Persona sin identificar",
    "matches.timeline_warning_title": "Conflicto de cronología — requiere revisión manual",
    "matches.timeline_warning_body": "La fecha de hallazgo es anterior a la fecha en que se vio por última vez a la persona, por lo que sobre el papel este emparejamiento es imposible. Aun así coincide muy alto en todos los demás detalles — una denuncia de desaparición tardía o inexacta puede causar esto. Verifique las fechas manualmente.",
    "matches.date_last_seen": "Visto por última vez",
    "matches.date_found": "Hallazgo",
    "review.status_label": "Estado",
    "review.status.new": "Nuevo",
    "review.status.investigating": "En investigación",
    "review.status.excluded": "Descartado",
    "review.status.confirmed": "Confirmado",
    "review.log_title": "Registro de investigación",
    "review.no_notes": "Aún no hay notas.",
    "matches.count_label": "coincidencia(s)",
    "matches.load_more": "Cargar más resultados",
    "matches.top_shown": "Mostrando las {shown} mejores de {total}",
    "form.contact_name": "Persona de contacto (nombre)",
    "form.contact_phone": "Teléfono de contacto",
    "form.contact_email": "Correo de contacto",
    "review.status_admin_only": "Solo el administrador de EuroFind puede cambiar este estado — añada una nota con sus conclusiones.",
    "imp.type_warn.ante": "Parece un cuerpo sin identificar — verifique antes de importarlo como persona desaparecida",
    "imp.type_warn.post": "Parece una persona desaparecida — verifique antes de importarla como cuerpo sin identificar",
    "imp.merged": "Fusionados",
    "imp.merged_note": "Duplicados — se complementó el caso existente en lugar de crear uno nuevo",
    "detail.merged_from": "Complementado desde",
    "help.title": "Manos amigas internacionales",
    "help.subtitle": "Ayude a EuroFind a crecer: recopile casos públicos de su país, rellene nuestras fichas y súbalas para su revisión.",
    "help.apply_tab": "Solicitar un código",
    "help.upload_tab": "Ya tengo un código",
    "help.photos": "Fotos de la persona (opcional, máx. 6)",
    "help.photos_count": "foto(s)",
    "help.thanks_stats": "Ya ha contribuido {total} ficha(s) en total — {approved} aprobada(s) y en línea en la plataforma.",
    "help.kind_corrected": "{n} ficha(s) se movieron automáticamente a la categoría correcta: el tipo de ficha del PDF no coincidía con su selección.",
    "help.thanks_milestone": "¡Muchísimas gracias! Con {approved} fichas aprobadas es una de nuestras ayudas más valiosas.",
    "help.photos_hint": "JPG/PNG — tras la aprobación, estas fotos se muestran con el caso en la plataforma.",
    "help.photos_single": "Las fotos solo pueden añadirse al subir una sola ficha a la vez, para saber a qué caso pertenecen.",
    "help.applied_next": "Una vez aprobado, recibirá su código personal — vuelva aquí y elija «Ya tengo un código» para subir sus fichas.",
    "help.name": "Nombre completo",
    "help.email": "Correo electrónico",
    "help.org": "Organización (opcional)",
    "help.scope": "¿Qué le gustaría cubrir?",
    "help.scope_hint": "p.ej. «todos los cuerpos sin identificar en Bulgaria»",
    "help.apply_btn": "Solicitar",
    "help.applied": "¡Gracias! Su solicitud está pendiente — el equipo de EuroFind le contactará con su código EuroFind personal.",
    "help.status_pending": "Su solicitud sigue pendiente — el equipo de EuroFind le contactará con su código personal.",
    "help.status_approved": "Su solicitud ya fue aprobada — use su código EuroFind en la pestaña de subida.",
    "help.status_rejected": "Su solicitud no fue aprobada. Contacte al equipo de EuroFind para más información.",
    "help.code": "Su código EuroFind",
    "help.kind": "¿Qué está subiendo?",
    "help.kind_am": "Personas desaparecidas (ficha verde)",
    "help.kind_pm": "Cuerpos sin identificar (ficha azul)",
    "help.templates": "Descargar las fichas",
    "help.tpl_am": "Ficha persona desaparecida",
    "help.tpl_pm": "Ficha persona sin identificar",
    "help.files": "Fichas PDF rellenadas (máx. 10)",
    "help.upload_btn": "Subir a EuroFind",
    "help.uploaded": "¡Recibido! Sus fichas esperan la revisión del equipo de EuroFind y aparecerán en la plataforma tras su aprobación.",
    "help.upload_more": "Subir más fichas",
    "help.how_title": "Cómo funciona",
    "help.how1": "Solicite con su nombre y correo electrónico.",
    "help.how2": "Tras la aprobación recibirá su código EuroFind personal y las fichas.",
    "help.how3": "Rellene una ficha por caso — su código EuroFind arriba — y súbalas aquí con su correo + código.",
    "help.how_photos": "Nunca pegue fotos en el PDF — añádalas en el formulario de subida: cada ficha tiene su propio recuadro de fotos (máx. 6), así cada foto queda vinculada al caso correcto.",
    "help.how4": "El equipo de EuroFind revisa cada ficha; los casos aprobados aparecen en la plataforma.",
    "help.landing_eyebrow": "Para voluntarios, estudiantes e investigadores",
    "help.landing_title": "Ayúdenos desde cualquier lugar de Europa",
    "help.landing_text": "Tome un país bajo su protección y envíe casos con nuestras fichas listas para usar. Cada ficha se revisa antes de aparecer en la plataforma.",
    "help.landing_cta": "Conviértase en mano amiga",
    "kind.ante": "Persona desaparecida",
    "kind.post": "Sin identificar",
    "notif.helper_apply_msg": "{author} se ha postulado como mano amiga — revise la solicitud",
    "notif.helper_upload_msg": "{author} subió {n} ficha(s) a la bandeja de espera",
    "detail.back_list": "Volver a los resultados",
    "browse.save_search": "Guardar búsqueda",
    "browse.saved": "Búsquedas guardadas",
    "browse.name_prompt": "Nombre de esta búsqueda:",
    "browse.saved_ok": "Búsqueda guardada",
    "browse.load_more": "Cargar más",
    "form.source_ref": "Referencia de origen",
    "match.new_hint": "Un match permanece «Nuevo» hasta que una organización implicada establece un estado o añade una nota.",
    "match.compare": "Lado a lado",
    "match.compare_title": "Comparación del match",
    "match.highmatch_title": "Match alto — ¿y ahora?",
    "match.highmatch_body": "Verifique ambas fichas con cuidado, contacte con la organización titular de la otra ficha (vea sus datos de contacto) y registre sus conclusiones abajo mediante el estado y las notas. La administración de EuroFind (BVM) recibe una alerta automática con matches de 85+ y puede coordinar entre países.",
    "notif.title": "Notificaciones",
    "notif.empty": "Aún no hay notificaciones.",
    "notif.note_msg": "{author} añadió una nota al match {a} × {b}",
    "notif.status_msg": "{author} cambió el match {a} × {b} a {status}",
    "review.note_placeholder": "Añadir una nota sobre esta coincidencia…",
    "review.add_note": "Añadir nota",
    "review.show_details": "Mostrar detalles",
    "review.hide_details": "Ocultar",
    "review.hide_excluded": "Ocultar coincidencias descartadas",
    "review.updated_by": "Actualizado por",
    "browse.title": "Explorar casos",
    "browse.sub": "Busque por nombre, país, ciudad, rasgos físicos o señas particulares.",
    "browse.search": "Buscar…",
    "browse.tab_post": "Personas sin identificar",
    "browse.tab_ante": "Personas desaparecidas",
    "browse.locked_title": "Inicie sesión para explorar casos",
    "browse.locked_sub": "Por respeto y privacidad, la exploración está reservada a cuentas verificadas.",
    "form.section.id": "Identificación",
    "form.section.demo": "Demografía",
    "form.section.physical": "Descripción física",
    "form.section.last_seen": "Visto por última vez",
    "form.section.found": "Lugar y fecha de hallazgo",
    "form.section.photo": "Foto",
    "form.full_name": "Nombre completo de la persona desaparecida *",
    "form.dob": "Fecha de nacimiento",
    "form.age_at_disappearance": "Edad en el momento de la desaparición",
    "form.age_unknown_dob": "O introduzca la edad manualmente (si se desconoce la fecha de nacimiento)",
    "form.height_min": "Altura (mín., cm)",
    "form.height_max": "Altura (máx., cm)",
    "form.ref_code": "Código de referencia interno (opcional)",
    "form.relation": "Su relación con la persona (opcional)",
    "form.age_min": "Edad estimada (mín.)",
    "form.age_max": "Edad estimada (máx.)",
    "form.age": "Edad",
    "form.gender": "Sexo",
    "form.gender.male": "Hombre",
    "form.gender.female": "Mujer",
    "form.gender.unknown": "Desconocido",
    "form.height": "Altura (cm)",
    "form.weight": "Peso (kg)",
    "form.hair": "Color de cabello",
    "form.eye": "Color de ojos",
    "attr.black": "Negro",
    "attr.brown": "Castaño",
    "attr.blond": "Rubio",
    "attr.red": "Pelirrojo",
    "attr.grey": "Gris",
    "attr.white": "Blanco",
    "attr.bald": "Calvo",
    "attr.blue": "Azul",
    "attr.green": "Verde",
    "attr.hazel": "Avellana",
    "attr.amber": "Ámbar",
    "tag.hair": "cabello",
    "tag.eyes": "ojos",
    "form.dob_day": "DD",
    "form.dob_month": "MM",
    "form.dob_year": "AAAA",
    "form.dob_hint": "Con solo el año es suficiente si se desconoce la fecha completa.",
    "detail.translate": "Traducir los detalles",
    "detail.show_original": "Mostrar original",
    "detail.translated_badge": "Traducción automática",
    "detail.translate_error": "Error de traducción. Inténtalo de nuevo.",
    "detail.photos_unsaved": "Cambios de fotos sin guardar",
    "detail.photos_save": "Guardar cambios",
    "detail.photos_cancel": "Cancelar",
    "match.field.age": "Rango de edad",
    "match.field.circumstances": "Circunstancias",
    "match.field.gender_partial": "Sexo desconocido (parcial)",
    "form.ethnicity": "Origen étnico",
    "form.marks": "Señas particulares (cicatrices, tatuajes, marcas de nacimiento)",
    "form.clothing": "Ropa o accesorios",
    "form.country": "País *",
    "form.location": "Ciudad o región",
    "form.date_seen": "Fecha de la última vez visto",
    "form.date_found": "Fecha del hallazgo",
    "form.pmi": "Tiempo estimado desde la muerte (días)",
    "form.description.ante": "Circunstancias y notas adicionales",
    "form.description.post": "Descripción del caso",
    "form.case_notes": "Notas internas",
    "form.photo_hint": "Use una fotografía clara e identificable. Máximo 8 MB. JPG, PNG o WEBP.",
    "form.upload": "Subir foto",
    "form.uploading": "Subiendo…",
    "form.submit.ante": "Enviar informe de persona desaparecida",
    "form.submit.post": "Guardar ficha de persona sin identificar",
    "form.saving": "Guardando…",
    "form.required_error": "Complete los campos obligatorios resaltados.",
    "new.ante.title": "Reportar una persona desaparecida",
    "new.post.title": "Añadir una ficha de persona sin identificar",
    "new.ante.sub": "Proporcione la mayor cantidad de detalles posible. Cada campo ayuda al motor a sugerir candidatos relevantes.",
    "new.post.sub": "Introduzca los datos estructurados del caso. La información solo es accesible para cuentas verificadas.",
    "dash.new_case": "Añadir una ficha",
    "choose.title": "¿Qué desea registrar?",
    "choose.sub": "Elija primero el tipo de ficha — el formulario se adapta a su elección.",
    "choose.post.title": "Persona sin identificar",
    "choose.post.desc": "Una persona fallecida cuya identidad se desconoce. No se necesita nombre — rellenará una ficha post mortem con un código de referencia.",
    "choose.ante.title": "Persona desaparecida",
    "choose.ante.desc": "Una persona denunciada como desaparecida. Rellenará una ficha ante mortem con su nombre y datos.",
    "choose.single": "Rellenar una ficha",
    "choose.bulk": "Importación masiva (CSV / URL)",
    "admin.eyebrow": "Admin · verificación",
    "admin.title": "Solicitudes de autoridad",
    "admin.sub": "Revise las cuentas profesionales que solicitan acceso. Solo las autoridades aprobadas pueden añadir o editar fichas de personas sin identificar.",
    "admin.pending": "Pendientes",
    "admin.approved": "Aprobadas",
    "admin.rejected": "Rechazadas",
    "admin.approve": "Aprobar",
    "admin.reject": "Rechazar",
    "admin.empty": "Nada en esta lista.",
    "invite.panel.title": "Enlaces de invitación",
    "invite.panel.sub": "Genere enlaces de un solo uso pre-aprobados y envíelos a colegas de confianza. Quien se registre por el enlace se convierte en autoridad activa de inmediato — sin más revisión administrativa.",
    "invite.new": "Nuevo enlace de invitación",
    "invite.create_title": "Crear enlace de invitación",
    "invite.restrict_email": "Restringir a un correo (opcional)",
    "invite.restrict_hint": "Si se establece, solo esta dirección podrá usar el enlace.",
    "invite.expires": "Expira en (días)",
    "invite.note": "Nota interna (opcional)",
    "invite.create_btn": "Crear enlace",
    "invite.creating": "Creando…",
    "invite.created_title": "Enlace de invitación creado",
    "invite.created_sub": "Comparta este enlace con su colega. Es de un solo uso y expira el",
    "invite.copy": "Copiar enlace",
    "invite.copied": "Copiado",
    "invite.revoke": "Revocar",
    "invite.done": "Listo",
    "invite.page_badge": "Invitación de autoridad pre-aprobada",
    "invite.page_title": "Configura tu cuenta de autoridad",
    "invite.page_sub": "Su cuenta se activará automáticamente al registrarse.",
    "invite.locked_hint": "Esta invitación está bloqueada a este correo.",
    "invite.submit": "Activar cuenta de autoridad",
    "invite.invalid_title": "Invitación no disponible",
    "invite.back_home": "Volver al inicio",
    "invite.verifying": "Verificando invitación…",
    "pending.title": "Su cuenta de autoridad está en revisión",
    "pending.rejected_title": "Cuenta no aprobada",
    "pending.sub": "Gracias por registrarse. Un administrador verificará sus credenciales profesionales antes de darle acceso para añadir casos y ver coincidencias. Normalmente tarda alrededor de un día hábil.",
    "pending.rejected_sub": "Un administrador ha revisado su solicitud y no ha podido aprobar su cuenta de autoridad. Si cree que es un error, contacte con los administradores de EuroFind con credenciales adicionales.",
    "family.status.title": "Su caso está siendo monitorizado",
    "family.status.body": "Su informe se compara de forma continua y confidencial con los registros de toda Europa. Para protegerle de la angustia, no mostramos aquí los detalles de las comparaciones. Su enlace se pondrá en contacto con usted directamente si se encuentra algo relevante.",
    "family.status.contact_title": "Su punto de contacto",
    "family.status.contact_sub": "Para cualquier pregunta sobre su informe, póngase en contacto con el enlace de su región.",
    "family.status.no_liaison": "Se está asignando un enlace dedicado para su región. Mientras tanto, póngase en contacto con su policía local o con la autoridad donde presentó su informe.",
    "edit.ante.title": "Editar informe de persona desaparecida",
    "edit.post.title": "Editar ficha de persona sin identificar",
    "edit.sub": "Edite cualquier campo a continuación. El código de referencia permanece igual.",
    "form.submit.save": "Guardar cambios",
    "common.edit": "Editar",
    "detail.eyebrow": "Detalle del caso",
    "detail.back": "Volver al panel",
    "detail.privacy_title": "Estos detalles se mantienen privados",
    "detail.privacy_body": "Para proteger a las familias de falsas esperanzas y angustia, los detalles de los registros no identificados no se muestran aquí. Si hay una novedad relevante en su caso, su enlace se pondrá en contacto con usted directamente.",
    "detail.view": "Ver",
    "detail.imported": "Registro importado",
    "detail.source": "Fuente",
    "detail.reported_by": "Su relación",
    "case.submitted_by": "Comunicado por",
    "detail.privacy_cta": "Ver el estado de su caso",
    "detail.copy_link": "Copiar enlace",
    "detail.copied": "Enlace copiado",
    "detail.print": "Imprimir / PDF",
    "detail.export_pdf": "Exportar PDF",
    "detail.pdf_error": "No se pudo generar el PDF.",
    "detail.photos_saved": "Fotos actualizadas.",
    "detail.photos_error": "No se pudieron actualizar las fotos.",
    "detail.photo_set_main": "Establecer como principal",
    "detail.photo_remove": "Eliminar foto",
    "detail.photo_add": "Añadir foto",
  },
  it: {
    "nav.home": "Home",
    "nav.dashboard": "Pannello",
    "nav.matches": "Corrispondenze",
    "nav.browse": "Sfoglia casi",
    "nav.admin": "Amministrazione",
    "nav.signin": "Accedi",
    "nav.signout": "Esci",
    "nav.create_account": "Crea account",
    "landing.eyebrow": "Persone scomparse e non identificate in Europa",
    "landing.hero_title": "Aiutiamo le famiglie a ritrovare chi hanno perso — e gli esperti a dare loro risposte.",
    "landing.hero_subtitle": "Le famiglie condividono ciò che sanno di una persona cara scomparsa. I team forensi condividono ciò che hanno trovato. EuroFind confronta discretamente i due — in tutta Europa — per individuare prima le possibili corrispondenze.",
    "landing.cta_family": "Sto cercando qualcuno",
    "landing.cta_authority": "Lavoro nella polizia o nella medicina legale",
    "landing.stat.countries": "Paesi coperti",
    "landing.stat.ante": "Segnalazioni di persone scomparse",
    "landing.stat.post": "Schede di persone non identificate",
    "landing.stat.users": "Membri verificati",
    "landing.sides_title": "Due lati, un unico obiettivo.",
    "landing.sides_subtitle": "Ogni parte aggiunge separatamente ciò che sa. EuroFind confronta i dettagli — mai nomi o identità — e segnala possibili corrispondenze da verificare a delle persone.",
    "landing.family_card_title": "Per le famiglie",
    "landing.authority_card_title": "Per le autorità",
    "landing.how_title": "Come funziona EuroFind",
    "landing.browse_btn": "Sfoglia le schede anonimizzate",
    "landing.ways_title": "Tre modi per far crescere EuroFind.",
    "landing.pos_title": "EuroFind collega ciò che confini, banche dati e decenni hanno tenuto separato.",
    "landing.pos_body": "Una piattaforma di ricerca europea indipendente, progettata per scoprire possibili collegamenti tra persone scomparse e resti umani non identificati.",
    "landing.pos_complement": "A complemento dei sistemi ufficiali di identificazione — mai in loro sostituzione: i possibili collegamenti vengono sempre sottoposti alle autorità competenti per la verifica.",
    "landing.ways_contribute_title": "Contribuire",
    "landing.ways_contribute_body": "Fornisci informazioni su casi idonei e la tua esperienza come partner autorizzato.",
    "landing.ways_contribute_link": "Registrati come autorità",
    "landing.ways_participate_title": "Partecipare",
    "landing.ways_participate_body": "Aiuta a verificare e strutturare le informazioni sui casi nel tuo paese.",
    "landing.ways_participate_link": "Diventa una mano amica",
    "landing.ways_fund_title": "Finanziare",
    "landing.ways_fund_body": "Sostieni l'infrastruttura, le tutele e la crescita del servizio.",
    "landing.ways_fund_link": "Mettiti in contatto con noi",
    "landing.footer": "EuroFind è uno strumento umanitario gratuito. Non sostituisce la polizia né le autorità nazionali. In caso di emergenza, o se hai informazioni urgenti, contatta prima la polizia locale.",
    "landing.trust_title": "Cercare risposte, insieme",
    "landing.trust_body": "Se sei qui perché una persona cara è scomparsa, ci dispiace profondamente. Non devi cercare da solo. EuroFind unisce con delicatezza ciò che sanno le famiglie e ciò che trovano i team forensi — con cura e senza false speranze. È gratuito ed è qui per te.",
    "landing.family_card_body": "Aggiungi una segnalazione di scomparsa: una foto, una descrizione fisica, gli abiti indossati l'ultima volta, segni particolari. Mantieni il controllo della tua segnalazione e consulti le possibili corrispondenze in privato.",
    "landing.family_b1": "Un modulo semplice, passo dopo passo",
    "landing.family_b2": "Suggerimenti di corrispondenza privati, solo per te",
    "landing.family_b3": "Sempre gratuito",
    "landing.family_link": "Crea un account famiglia",
    "landing.authority_card_body": "Aggiungi schede di persone non identificate: fascia d'età stimata, sesso, tratti identificativi, luogo e data del ritrovamento. Sfoglia tutte le schede ed esamina le corrispondenze suggerite accanto alla segnalazione di scomparsa.",
    "landing.authority_b1": "Raccolta dati forense strutturata",
    "landing.authority_b2": "Un database consultabile oltre i confini",
    "landing.authority_b3": "Suggerimenti di corrispondenza chiari e basati su regole",
    "landing.authority_link": "Richiedi un account autorità",
    "landing.step1_title": "I dati vengono inseriti",
    "landing.step1_desc": "Le famiglie e i team forensi aggiungono ciò che sanno tramite moduli semplici e dedicati.",
    "landing.step2_title": "I casi vengono confrontati",
    "landing.step2_desc": "Un motore chiaro e basato su regole valuta le possibili corrispondenze in base a età, sesso, paese, tratti fisici e segni particolari.",
    "landing.step3_title": "Sono le persone a decidere",
    "landing.step3_desc": "Le corrispondenze sono suggerimenti, mai conclusioni. Gli esperti forensi, le famiglie e le autorità competenti decidono insieme.",
    "auth.signin_title": "Accedi",
    "auth.signin_sub": "Accedi al tuo pannello e all'area corrispondenze.",
    "auth.email": "Email",
    "auth.password": "Password",
    "auth.no_account": "Nuovo qui?",
    "auth.create_account": "Crea un account",
    "auth.already_account": "Hai già un account?",
    "auth.signing_in": "Accesso…",
    "auth.withdrawn_notice": "Il tuo accesso è stato revocato da un amministratore. Contatta la tua organizzazione per i dettagli.",
    "auth.creating": "Creazione account…",
    "auth.forgot": "Password dimenticata?",
    "forgot.title": "Reimposta la password",
    "forgot.sub": "Inserisci l'email del tuo account e ti invieremo un link per impostare una nuova password.",
    "forgot.submit": "Invia il link",
    "forgot.sending": "Invio…",
    "forgot.sent_title": "Controlla la tua casella di posta",
    "forgot.sent_sub": "Se esiste un account con quell'email, il link di reimpostazione è in arrivo. Scade tra 1 ora.",
    "forgot.back_to_login": "Torna all'accesso",
    "reset.title": "Scegli una nuova password",
    "reset.sub": "Inserisci una nuova password per il tuo account.",
    "reset.password": "Nuova password",
    "reset.confirm": "Conferma password",
    "reset.submit": "Aggiorna password",
    "reset.updating": "Aggiornamento…",
    "reset.success_title": "Password aggiornata",
    "reset.success_sub": "Ora puoi accedere con la tua nuova password.",
    "reset.mismatch": "Le password non corrispondono.",
    "reset.invalid": "Questo link di reimpostazione non è valido o è scaduto. Richiedine uno nuovo.",
    "reset.no_token": "Nessun token di reimpostazione trovato. Apri il link dalla tua email.",
    "reset.go_login": "Vai all'accesso",
    "auth.register_title": "Crea il tuo account",
    "auth.register_sub": "Scegli come userai EuroFind.",
    "auth.iam": "Sono…",
    "auth.role.family": "Familiare",
    "auth.role.family_sub": "Sto cercando una persona cara scomparsa",
    "auth.role.authority": "Autorità / Esperto forense",
    "auth.role.authority_sub": "Lavoro su casi di corpi non identificati",
    "auth.full_name": "Nome completo",
    "auth.organisation": "Organizzazione",
    "auth.optional": "(opzionale)",
    "auth.password_hint": "Almeno 6 caratteri.",
    "auth.authority_notice": "Gli account autorità vengono verificati da un amministratore prima di poter aggiungere casi. Puoi accedere subito — l'aggiunta di schede si sblocca una volta approvato il tuo account, di solito entro un giorno lavorativo.",
    "dash.welcome": "Benvenuto,",
    "dash.workspace.family": "Spazio famiglia",
    "dash.workspace.authority": "Spazio autorità",
    "dash.workspace.admin": "Spazio amministrazione",
    "dash.family_sub": "Gestisci le segnalazioni che hai inviato e verifica le possibili corrispondenze.",
    "dash.authority_sub": "Aggiungi e rivedi le schede di persone non identificate e confrontale con le segnalazioni di persone scomparse.",
    "dash.admin_sub": "Rivedi l'attività della piattaforma e approva gli account professionali.",
    "dash.new_ante": "Segnala una persona scomparsa",
    "dash.new_post": "Aggiungi una scheda di persona non identificata",
    "dash.review_auth": "Esamina le autorità",
    "dash.matches": "Corrispondenze",
    "dash.your_reports": "Le tue segnalazioni di persone scomparse",
    "dash.your_post": "Le tue schede di persone non identificate",
    "dash.ante_all": "Segnalazioni di persone scomparse in Europa",
    "dash.mine_only": "Solo i miei",
    "dash.all_reports": "Tutte le segnalazioni",
    "dash.bulk_select": "Eliminazione multipla",
    "dash.bulk_cancel": "Annulla",
    "dash.select_all": "Seleziona tutto",
    "dash.selected": "selezionati",
    "dash.delete_selected": "Elimina selezionati",
    "dash.bulk_confirm": "Eliminare definitivamente i casi selezionati? L'operazione non può essere annullata.",
    "dash.bulk_deleted": "casi eliminati",
    "dash.filter_contributor": "Contributore",
    "dash.filter_all_users": "Tutti gli utenti",
    "dash.filter_added": "Aggiunto",
    "dash.filter_any_time": "Qualsiasi data",
    "dash.filter_today": "Oggi",
    "dash.filter_7d": "Ultimi 7 giorni",
    "dash.filter_30d": "Ultimi 30 giorni",
    "dash.case_status": "Stato del caso",
    "dash.brand_title": "Fai parte della rete EuroFind",
    "dash.brand_body": "Una rete umanitaria che collega famiglie e team forensi in tutta Europa. Mostra il marchio EuroFind sul tuo sito o sui tuoi materiali per far sapere che partecipi.",
    "dash.download_logo": "Scarica il logo",
    "dash.stat_ante_all": "Segnalazioni di persone scomparse (tutte)",
    "dash.stat_post": "Schede di persone non identificate",
    "dash.stat_matches": "Corrispondenze potenziali",
    "dash.monitored": "Monitorato",
    "dash.my_imports": "Le mie importazioni",
    "dash.bulk_import": "Importazione in blocco",
    "dash.reports_count": "segnalazioni",
    "dash.ante_empty": "Ancora nessuna segnalazione di persona scomparsa",
    "dash.ante_empty_sub": "Quando le famiglie aggiungono casi, appariranno qui.",
    "dash.no_reports": "Nessuna segnalazione ancora",
    "dash.no_reports_sub": "Inizia aggiungendo una segnalazione di persona scomparsa. I tuoi dati restano privati e vengono confrontati solo con schede di persone non identificate.",
    "dash.no_post": "Ancora nessuna scheda di persona non identificata",
    "dash.no_post_sub": "Aggiungi i dettagli di una persona non identificata per consentire il confronto transfrontaliero.",
    "common.delete": "Elimina",
    "common.loading": "Caricamento…",
    "common.sign_out": "Esci",
    "matches.eyebrow": "Motore di confronto",
    "matches.title": "Possibili corrispondenze",
    "matches.sub": "Sono suggerimenti da un confronto chiaro e basato su regole — non identificazioni confermate. Ogni accoppiamento deve essere verificato da una persona. Un punteggio più alto significa solo che coincidono più dettagli.",
    "matches.min_score": "Punteggio minimo",
    "matches.empty_title": "Nessuna corrispondenza potenziale",
    "matches.empty_sub": "Man mano che arrivano più dati su persone scomparse e non identificate, qui appariranno suggerimenti.",
    "matches.score": "Punteggio",
    "matches.circ_hits": "Circostanze coincidenti",
    "review.read_only": "Solo le organizzazioni coinvolte in questa corrispondenza (o un amministratore) possono modificarne lo stato o aggiungere note.",
    "dash.download_logo_transparent": "Logo trasparente",
    "imp.eyebrow": "Importazione dati",
    "imp.title.ante": "Importazione in blocco — persone scomparse",
    "imp.title.post": "Importazione in blocco — persone non identificate",
    "imp.tab.ante": "Persone scomparse",
    "imp.tab.post": "Persone non identificate",
    "imp.desc.ante": "Carica un CSV o XLSX di schede pubbliche di persone scomparse (conformi al GDPR). Il modello raccoglie il profilo fisico completo così il motore di corrispondenza può confrontare con i corpi non identificati. Ogni riga riceve automaticamente un codice di riferimento EF.",
    "imp.desc.post": "Carica un CSV o XLSX di schede di corpi non identificati (ad es. da un'autorità, una ONG o un giornalista investigativo). Solo COUNTRY è obbligatorio per riga; il codice di riferimento viene generato automaticamente se lasciato vuoto.",
    "imp.required.ante": "Colonne (solo «Full name» e «Last seen - Country» sono obbligatorie — il resto è facoltativo):",
    "imp.required.post": "Colonne (solo COUNTRY è obbligatoria — il resto è facoltativo):",
    "imp.template": "Modello",
    "imp.drag": "Trascina qui un file, oppure",
    "imp.choose_file": "Scegli file",
    "imp.importing": "Importazione…",
    "imp.import_records": "Importa schede",
    "imp.pdf.title": "Importa da un PDF (IA)",
    "imp.pdf.desc": "Carica un fascicolo in PDF (avviso di polizia, rapporto o pagina web salvata). L'IA legge il testo — anche documenti scansionati —, estrae i dati e le foto e propone schede da verificare prima dell'importazione.",
    "imp.pdf.analyse": "Leggi PDF",
    "imp.pdf.hint": "Lettura del PDF ed estrazione dei dati — può richiedere fino a un minuto.",
    "imp.url.title": "Importa da una pagina web (IA)",
    "imp.url.desc": "Incolla l'indirizzo di una pagina riepilogativa pubblicata (ad es. un elenco della polizia). L'IA la legge, segue le pagine dei singoli casi e propone schede che puoi verificare prima dell'importazione. Usa solo pagine che la tua organizzazione è autorizzata a riutilizzare.",
    "imp.url.label": "URL della pagina",
    "imp.url.max": "Casi max",
    "imp.url.analyse": "Analizza pagina",
    "imp.url.analysing": "Analisi in corso…",
    "imp.url.hint": "Lettura della pagina e dei dettagli dei casi — può richiedere fino a un minuto.",
    "imp.url.found": "scheda/e trovata/e. Spunta quelle che vuoi importare.",
    "imp.url.select_all": "Seleziona tutto",
    "imp.url.deselect_all": "Deseleziona tutto",
    "imp.url.import_btn": "Importa selezionati",
    "imp.url.empty": "L'IA non è riuscita a estrarre alcuna scheda da quella pagina. Potrebbe caricare i dati tramite JavaScript o usare un layout insolito. Prova un'altra pagina riepilogativa, oppure usa l'importazione CSV qui sopra.",
    "imp.url.imported": "Importate:",
    "imp.skipped_lc": "saltata/e",
    "imp.errors_lc": "errore/i",
    "imp.summary": "Riepilogo importazione",
    "imp.created": "Create",
    "imp.skipped": "Saltate (vuote)",
    "imp.errors": "Errori",
    "imp.all_ok": "Tutte le righe valide sono state importate correttamente.",
    "imp.some_failed": "Alcune righe non sono state importate:",
    "imp.row": "Riga",
    "thanks.title": "Grazie — abbiamo ricevuto la sua segnalazione",
    "thanks.body1": "Grazie per averci affidato la sua segnalazione. Sappiamo quanto questo passo possa essere difficile.",
    "thanks.body2": "Le sue informazioni saranno confrontate, in modo riservato, con i dati condivisi da servizi forensi e autorità di tutta Europa.",
    "thanks.body3": "In caso di sviluppi rilevanti, noi — o uno dei nostri referenti locali — la contatteremo personalmente. Non deve fare altro.",
    "thanks.body4": "Non possiamo fare promesse sull'esito, ma ogni segnalazione rafforza la ricerca.",
    "thanks.back": "Torna alla mia dashboard",
    "matches.ante": "Persona scomparsa",
    "matches.post": "Persona non identificata",
    "matches.timeline_warning_title": "Conflitto cronologico — verifica manuale necessaria",
    "matches.timeline_warning_body": "La data di ritrovamento è precedente alla data in cui la persona è stata vista l'ultima volta, quindi sulla carta questo abbinamento è impossibile. Tuttavia ottiene un punteggio molto alto in tutti gli altri dettagli — una segnalazione di scomparsa tardiva o inesatta può causarlo. Verificare le date manualmente.",
    "matches.date_last_seen": "Vista l'ultima volta",
    "matches.date_found": "Ritrovamento",
    "review.status_label": "Stato",
    "review.status.new": "Nuovo",
    "review.status.investigating": "In esame",
    "review.status.excluded": "Escluso",
    "review.status.confirmed": "Confermato",
    "review.log_title": "Registro delle indagini",
    "review.no_notes": "Ancora nessuna nota.",
    "matches.count_label": "corrispondenza/e",
    "matches.load_more": "Carica altri risultati",
    "matches.top_shown": "Mostrate le prime {shown} di {total}",
    "form.contact_name": "Persona di contatto (nome)",
    "form.contact_phone": "Telefono di contatto",
    "form.contact_email": "E-mail di contatto",
    "review.status_admin_only": "Solo l'amministratore EuroFind può modificare questo stato — aggiungi una nota con le tue osservazioni.",
    "imp.type_warn.ante": "Sembra un corpo non identificato — verificare prima di importarlo come persona scomparsa",
    "imp.type_warn.post": "Sembra una persona scomparsa — verificare prima di importarla come corpo non identificato",
    "imp.merged": "Uniti",
    "imp.merged_note": "Duplicati — il caso esistente è stato integrato invece di crearne uno nuovo",
    "detail.merged_from": "Integrato da",
    "help.title": "Mani amiche internazionali",
    "help.subtitle": "Aiuta EuroFind a crescere: raccogli casi pubblici del tuo paese, compila le nostre schede e caricale per la revisione.",
    "help.apply_tab": "Richiedi un codice",
    "help.upload_tab": "Ho già un codice",
    "help.photos": "Foto della persona (opzionale, max 6)",
    "help.photos_count": "foto",
    "help.thanks_stats": "Hai contribuito finora {total} scheda/e in totale — {approved} approvate e online sulla piattaforma.",
    "help.kind_corrected": "{n} scheda/e sono state spostate automaticamente nella categoria giusta: il tipo di scheda nel PDF non corrispondeva alla tua selezione.",
    "help.thanks_milestone": "Un grazie di cuore! Con {approved} schede approvate sei uno dei nostri aiutanti più preziosi.",
    "help.photos_hint": "JPG/PNG — dopo l'approvazione, queste foto vengono mostrate con il caso sulla piattaforma.",
    "help.photos_single": "Le foto possono essere aggiunte solo caricando una scheda alla volta, così sappiamo a quale caso appartengono.",
    "help.applied_next": "Una volta approvato riceverai il tuo codice personale — torna qui e scegli «Ho già un codice» per caricare le tue schede.",
    "help.name": "Nome completo",
    "help.email": "Indirizzo e-mail",
    "help.org": "Organizzazione (facoltativo)",
    "help.scope": "Cosa vorresti coprire?",
    "help.scope_hint": "es. «tutti i corpi non identificati in Bulgaria»",
    "help.apply_btn": "Candidati",
    "help.applied": "Grazie! La tua candidatura è in attesa — il team EuroFind ti contatterà con il tuo codice EuroFind personale.",
    "help.status_pending": "La tua candidatura è ancora in attesa — il team EuroFind ti contatterà con il tuo codice personale.",
    "help.status_approved": "La tua candidatura è già stata approvata — usa il tuo codice EuroFind nella scheda di caricamento.",
    "help.status_rejected": "La tua candidatura non è stata approvata. Contatta il team EuroFind per maggiori informazioni.",
    "help.code": "Il tuo codice EuroFind",
    "help.kind": "Cosa stai caricando?",
    "help.kind_am": "Persone scomparse (scheda verde)",
    "help.kind_pm": "Corpi non identificati (scheda blu)",
    "help.templates": "Scarica le schede",
    "help.tpl_am": "Scheda persona scomparsa",
    "help.tpl_pm": "Scheda persona non identificata",
    "help.files": "Schede PDF compilate (max 10)",
    "help.upload_btn": "Carica su EuroFind",
    "help.uploaded": "Ricevuto! Le tue schede sono in attesa di revisione da parte del team EuroFind e appariranno sulla piattaforma dopo l'approvazione.",
    "help.upload_more": "Carica altre schede",
    "help.how_title": "Come funziona",
    "help.how1": "Candidati con nome e indirizzo e-mail.",
    "help.how2": "Dopo l'approvazione ricevi il tuo codice EuroFind personale e le schede.",
    "help.how3": "Compila una scheda per caso — il tuo codice EuroFind in alto — e caricale qui con e-mail + codice.",
    "help.how_photos": "Non incollare mai foto nel PDF — aggiungile nel modulo di caricamento: ogni scheda ha il proprio riquadro foto (max 6), così ogni foto resta collegata al caso giusto.",
    "help.how4": "Il team EuroFind controlla ogni scheda; i casi approvati appaiono sulla piattaforma.",
    "help.landing_eyebrow": "Per volontari, studenti e ricercatori",
    "help.landing_title": "Aiutaci da ovunque in Europa",
    "help.landing_text": "Prendi un paese sotto la tua ala e invia casi con le nostre schede pronte all'uso. Ogni scheda viene controllata prima di apparire sulla piattaforma.",
    "help.landing_cta": "Diventa una mano amica",
    "kind.ante": "Persona scomparsa",
    "kind.post": "Non identificato",
    "notif.helper_apply_msg": "{author} si è candidato/a come mano amica — controlla la candidatura",
    "notif.helper_upload_msg": "{author} ha caricato {n} scheda/e nella casella d'attesa",
    "detail.back_list": "Torna ai risultati",
    "browse.save_search": "Salva ricerca",
    "browse.saved": "Ricerche salvate",
    "browse.name_prompt": "Dai un nome a questa ricerca:",
    "browse.saved_ok": "Ricerca salvata",
    "browse.load_more": "Carica altri",
    "form.source_ref": "Riferimento fonte",
    "match.new_hint": "Un match resta «Nuovo» finché un'organizzazione coinvolta non imposta uno stato o aggiunge una nota.",
    "match.compare": "Fianco a fianco",
    "match.compare_title": "Confronto del match",
    "match.highmatch_title": "Match elevato — e adesso?",
    "match.highmatch_body": "Verifica attentamente entrambe le schede, contatta l'organizzazione che detiene l'altra scheda (vedi i suoi contatti) e registra le tue conclusioni qui sotto tramite stato e note. L'amministrazione EuroFind (BVM) viene avvisata automaticamente per i match da 85+ e può coordinare tra paesi.",
    "notif.title": "Notifiche",
    "notif.empty": "Ancora nessuna notifica.",
    "notif.note_msg": "{author} ha aggiunto una nota al match {a} × {b}",
    "notif.status_msg": "{author} ha impostato il match {a} × {b} su {status}",
    "review.note_placeholder": "Aggiungi una nota su questa corrispondenza…",
    "review.add_note": "Aggiungi nota",
    "review.show_details": "Mostra dettagli",
    "review.hide_details": "Nascondi",
    "review.hide_excluded": "Nascondi corrispondenze escluse",
    "review.updated_by": "Aggiornato da",
    "browse.title": "Sfoglia casi",
    "browse.sub": "Cerca per nome, paese, città, tratti fisici o segni particolari.",
    "browse.search": "Cerca…",
    "browse.tab_post": "Persone non identificate",
    "browse.tab_ante": "Persone scomparse",
    "browse.locked_title": "Accedi per sfogliare i casi",
    "browse.locked_sub": "Per rispetto e privacy, la consultazione è riservata agli account verificati.",
    "form.section.id": "Identificazione",
    "form.section.demo": "Demografia",
    "form.section.physical": "Descrizione fisica",
    "form.section.last_seen": "Visto l'ultima volta",
    "form.section.found": "Luogo e data del ritrovamento",
    "form.section.photo": "Foto",
    "form.full_name": "Nome completo della persona scomparsa *",
    "form.ref_code": "Codice di riferimento interno (opzionale)",
    "form.relation": "Il tuo rapporto con la persona (opzionale)",
    "form.age_min": "Età stimata (min)",
    "form.age_max": "Età stimata (max)",
    "form.age": "Età",
    "form.dob": "Data di nascita",
    "form.age_at_disappearance": "Età alla scomparsa",
    "form.age_unknown_dob": "Oppure inserisci l'età manualmente (se la data di nascita è sconosciuta)",
    "form.height_min": "Altezza (min, cm)",
    "form.height_max": "Altezza (max, cm)",
    "form.gender": "Sesso",
    "form.gender.male": "Uomo",
    "form.gender.female": "Donna",
    "form.gender.unknown": "Sconosciuto",
    "form.height": "Altezza (cm)",
    "form.weight": "Peso (kg)",
    "form.hair": "Colore dei capelli",
    "form.eye": "Colore degli occhi",
    "attr.black": "Nero",
    "attr.brown": "Castano",
    "attr.blond": "Biondo",
    "attr.red": "Rosso",
    "attr.grey": "Grigio",
    "attr.white": "Bianco",
    "attr.bald": "Calvo",
    "attr.blue": "Blu",
    "attr.green": "Verde",
    "attr.hazel": "Nocciola",
    "attr.amber": "Ambra",
    "tag.hair": "capelli",
    "tag.eyes": "occhi",
    "form.dob_day": "GG",
    "form.dob_month": "MM",
    "form.dob_year": "AAAA",
    "form.dob_hint": "Basta l'anno se la data completa non è nota.",
    "detail.translate": "Traduci i dettagli",
    "detail.show_original": "Mostra originale",
    "detail.translated_badge": "Traduzione automatica",
    "detail.translate_error": "Traduzione non riuscita. Riprova.",
    "detail.photos_unsaved": "Modifiche alle foto non salvate",
    "detail.photos_save": "Salva modifiche",
    "detail.photos_cancel": "Annulla",
    "match.field.age": "Fascia d'età",
    "match.field.circumstances": "Circostanze",
    "match.field.gender_partial": "Sesso sconosciuto (parziale)",
    "form.ethnicity": "Origine etnica",
    "form.marks": "Segni particolari (cicatrici, tatuaggi, voglie)",
    "form.clothing": "Abbigliamento o accessori",
    "form.country": "Paese *",
    "form.location": "Città o regione",
    "form.date_seen": "Data dell'ultimo avvistamento",
    "form.date_found": "Data del ritrovamento",
    "form.pmi": "Tempo stimato dalla morte (giorni)",
    "form.description.ante": "Circostanze e note aggiuntive",
    "form.description.post": "Descrizione del caso",
    "form.case_notes": "Note interne",
    "form.photo_hint": "Usa una fotografia chiara e identificabile. Max 8 MB. JPG, PNG o WEBP.",
    "form.upload": "Carica foto",
    "form.uploading": "Caricamento…",
    "form.submit.ante": "Invia segnalazione",
    "form.submit.post": "Salva scheda di persona non identificata",
    "form.saving": "Salvataggio…",
    "form.required_error": "Completa i campi obbligatori evidenziati.",
    "new.ante.title": "Segnala una persona scomparsa",
    "new.post.title": "Aggiungi una scheda di persona non identificata",
    "new.ante.sub": "Fornisci il maggior numero possibile di dettagli accurati. Ogni campo aiuta il motore a proporre candidati pertinenti.",
    "new.post.sub": "Inserisci i dati strutturati del caso. Le informazioni sono accessibili solo agli account verificati.",
    "dash.new_case": "Aggiungi una scheda",
    "choose.title": "Cosa vuoi registrare?",
    "choose.sub": "Scegli prima il tipo di scheda — il modulo si adatta alla tua scelta.",
    "choose.post.title": "Persona non identificata",
    "choose.post.desc": "Una persona deceduta la cui identità è sconosciuta. Nessun nome richiesto — compilerai una scheda post mortem con un codice di riferimento.",
    "choose.ante.title": "Persona scomparsa",
    "choose.ante.desc": "Una persona segnalata come scomparsa. Compilerai una scheda ante mortem con nome e dettagli.",
    "choose.single": "Compila una scheda",
    "choose.bulk": "Importazione in blocco (CSV / URL)",
    "admin.eyebrow": "Amministrazione · verifica",
    "admin.title": "Richieste di accesso autorità",
    "admin.sub": "Esamina gli account professionali che richiedono l'accesso. Solo le autorità approvate possono aggiungere o modificare schede di persone non identificate.",
    "admin.pending": "In attesa",
    "admin.approved": "Approvati",
    "admin.rejected": "Rifiutati",
    "admin.approve": "Approva",
    "admin.reject": "Rifiuta",
    "admin.empty": "Nulla in questo elenco.",
    "invite.panel.title": "Link di invito",
    "invite.panel.sub": "Genera link monouso pre-approvati e invialigli a colleghi di fiducia. Chi si registra tramite il link diventa subito un'autorità attiva — nessuna ulteriore revisione amministrativa necessaria.",
    "invite.new": "Nuovo link di invito",
    "invite.create_title": "Crea link di invito",
    "invite.restrict_email": "Limita a un'email (opzionale)",
    "invite.restrict_hint": "Se impostato, solo questo indirizzo può usare il link.",
    "invite.expires": "Scade tra (giorni)",
    "invite.note": "Nota interna (opzionale)",
    "invite.create_btn": "Crea link",
    "invite.creating": "Creazione…",
    "invite.created_title": "Link di invito creato",
    "invite.created_sub": "Condividi questo link con il tuo collega. È monouso e scade il",
    "invite.copy": "Copia link",
    "invite.copied": "Copiato",
    "invite.revoke": "Revoca",
    "invite.done": "Fine",
    "invite.page_badge": "Invito di autorità pre-approvato",
    "invite.page_title": "Configura il tuo account autorità",
    "invite.page_sub": "Il tuo account sarà attivato automaticamente al momento della registrazione.",
    "invite.locked_hint": "Questo invito è vincolato a questa email.",
    "invite.submit": "Attiva l'account autorità",
    "invite.invalid_title": "Invito non disponibile",
    "invite.back_home": "Torna alla home",
    "invite.verifying": "Verifica invito…",
    "pending.title": "Il tuo account autorità è in revisione",
    "pending.rejected_title": "Account non approvato",
    "pending.sub": "Grazie per esserti registrato. Un amministratore verificherà le tue credenziali professionali prima di darti accesso all'aggiunta di casi e alle corrispondenze. Di solito richiede circa un giorno lavorativo.",
    "pending.rejected_sub": "Un amministratore ha esaminato la tua richiesta e non ha potuto approvare il tuo account autorità. Se ritieni sia un errore, contatta gli amministratori di EuroFind con credenziali aggiuntive.",
    "family.status.title": "Il tuo caso è monitorato",
    "family.status.body": "La tua segnalazione viene confrontata in modo continuo e riservato con i registri di tutta Europa. Per proteggerti dall'angoscia, qui non mostriamo i dettagli dei confronti. Il tuo referente ti contatterà direttamente se viene trovato qualcosa di rilevante.",
    "family.status.contact_title": "Il tuo punto di contatto",
    "family.status.contact_sub": "Per qualsiasi domanda sulla tua segnalazione, contatta il referente della tua regione.",
    "family.status.no_liaison": "Un referente dedicato per la tua regione è in fase di assegnazione. Nel frattempo, contatta la polizia locale o l'autorità presso cui hai presentato la segnalazione.",
    "edit.ante.title": "Modifica segnalazione",
    "edit.post.title": "Modifica scheda di persona non identificata",
    "edit.sub": "Modifica qualsiasi campo qui sotto. Il codice di riferimento resta invariato.",
    "form.submit.save": "Salva modifiche",
    "common.edit": "Modifica",
    "detail.eyebrow": "Dettaglio del caso",
    "detail.back": "Torna al pannello",
    "detail.privacy_title": "Questi dettagli restano riservati",
    "detail.privacy_body": "Per proteggere le famiglie da false speranze e angoscia, i dettagli dei registri non identificati non vengono mostrati qui. In caso di sviluppi rilevanti sul tuo caso, il tuo referente ti contatterà direttamente.",
    "detail.view": "Vedi",
    "detail.imported": "Record importato",
    "detail.source": "Fonte",
    "case.submitted_by": "Segnalato da",
    "detail.reported_by": "Il tuo rapporto",
    "detail.privacy_cta": "Vedi lo stato del tuo caso",
    "detail.copy_link": "Copia link",
    "detail.copied": "Link copiato",
    "detail.print": "Stampa / PDF",
    "detail.export_pdf": "Esporta PDF",
    "detail.pdf_error": "Impossibile generare il PDF.",
    "detail.photos_saved": "Foto aggiornate.",
    "detail.photos_error": "Impossibile aggiornare le foto.",
    "detail.photo_set_main": "Imposta come principale",
    "detail.photo_remove": "Rimuovi foto",
    "detail.photo_add": "Aggiungi foto",
  },
};

// Canonical physical-attribute values stored in the database (English keys).
const ATTR_VALUES = new Set(["black", "brown", "blond", "red", "grey", "white", "bald", "blue", "green", "hazel", "amber"]);

export function attrLabel(t, value) {
  if (!value) return value;
  const k = String(value).trim().toLowerCase();
  return ATTR_VALUES.has(k) ? t("attr." + k) : value;
}

const MATCH_FIELD_KEYS = {
  gender: "form.gender",
  age: "match.field.age",
  country: "form.country",
  height: "form.height",
  hair_color: "form.hair",
  eye_color: "form.eye",
  ethnicity: "form.ethnicity",
  distinguishing_marks: "form.marks",
  gender_partial: "match.field.gender_partial",
  circumstances: "match.field.circumstances",
};

export const fieldLabel = (t, field) => (MATCH_FIELD_KEYS[field] ? t(MATCH_FIELD_KEYS[field]) : field);

const I18nContext = createContext(null);

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    const stored = typeof window !== "undefined" ? localStorage.getItem("eurofind.lang") : null;
    if (stored && dict[stored]) return stored;
    const browser = typeof navigator !== "undefined" ? navigator.language.slice(0, 2) : "en";
    return dict[browser] ? browser : "en";
  });

  const setLang = useCallback((code) => {
    if (!dict[code]) return;
    localStorage.setItem("eurofind.lang", code);
    setLangState(code);
  }, []);

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const t = useCallback(
    (key, fallback) => {
      const v = dict[lang]?.[key];
      if (v != null) return v;
      const en = dict.en?.[key];
      if (en != null) return en;
      return fallback != null ? fallback : key;
    },
    [lang]
  );

  return (
    <I18nContext.Provider value={{ lang, setLang, t, langs: LANGS }}>
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be inside I18nProvider");
  return ctx;
}
